import { templateSplitApi as api } from './template.api';

export const addTagTypes = [
  'Profile',
  'User',
  'Companies',
  'Employee',
  'Group',
  'File Import',
  'Task Template',
  'Task',
  'Files',
  'PublicAPI',
  'Employee Onboarding',
  'Mobile',
  'Admin',
  'Public API Employee',
  'Public API Group',
  'Public API Vacation',
  'Public API Universal Document',
  'Diia',
  'Employee Onboarding Public',
] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getProfile: build.query<GetProfileResponse, GetProfileArg>({
        query: () => ({ url: `/api/internal/v1/profile` }),
        providesTags: ['Profile'],
      }),
      getAuthorizedUser: build.query<
        GetAuthorizedUserResponse,
        GetAuthorizedUserArg
      >({
        query: () => ({ url: `/api/internal/v1/user/me` }),
        providesTags: ['User'],
      }),
      updateAuthorizedUser: build.mutation<
        UpdateAuthorizedUserResponse,
        UpdateAuthorizedUserArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/user/me`,
          method: 'PUT',
          body: queryArg,
        }),
        invalidatesTags: ['User'],
      }),
      updateAuthorizedUserPhoto: build.mutation<
        UpdateAuthorizedUserPhotoResponse,
        UpdateAuthorizedUserPhotoArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/user/me/photo`,
          method: 'POST',
          body: queryArg,
        }),
        invalidatesTags: ['User'],
      }),
      getUserConfig: build.query<GetUserConfigResponse, GetUserConfigArg>({
        query: () => ({ url: `/api/internal/v1/user/config` }),
        providesTags: ['User'],
      }),
      updateUserConfig: build.mutation<
        UpdateUserConfigResponse,
        UpdateUserConfigArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/user/config`,
          method: 'PUT',
          body: queryArg,
        }),
        invalidatesTags: ['User'],
      }),
      getUserEmailNotificationSettings: build.query<
        GetUserEmailNotificationSettingsResponse,
        GetUserEmailNotificationSettingsArg
      >({
        query: () => ({ url: `/api/internal/v1/user/email-notifications` }),
        providesTags: ['User'],
      }),
      updateUserEmailNotificationSetting: build.mutation<
        UpdateUserEmailNotificationSettingResponse,
        UpdateUserEmailNotificationSettingArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/user/email-notifications/${queryArg.emailType}`,
          method: 'PUT',
          body: queryArg.userEmailNotificationSettingUpdateSerializer,
        }),
        invalidatesTags: ['User'],
      }),
      createUserRole: build.mutation<CreateUserRoleResponse, CreateUserRoleArg>(
        {
          query: (queryArg) => ({
            url: `/api/internal/v1/company/user-role`,
            method: 'POST',
            body: queryArg,
          }),
          invalidatesTags: ['Companies'],
        },
      ),
      getUserCompanies: build.query<
        GetUserCompaniesResponse,
        GetUserCompaniesArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/company/user-role`,
          params: {
            all: queryArg.all,
            id__in: queryArg.idIn,
            id__not_in: queryArg.idNotIn,
            page_num: queryArg.pageNum,
            page_size: queryArg.pageSize,
            active_status__in: queryArg.activeStatusIn,
            search: queryArg.search,
            allow_crm_import: queryArg.allowCrmImport,
          },
        }),
        providesTags: ['Companies'],
      }),
      updateUserRole: build.mutation<UpdateUserRoleResponse, UpdateUserRoleArg>(
        {
          query: (queryArg) => ({
            url: `/api/internal/v1/company/user-role/${queryArg.userRoleId}`,
            method: 'PATCH',
            body: queryArg.updateUserRoleSerializer,
          }),
          invalidatesTags: ['Companies'],
        },
      ),
      listCompanyUsers: build.query<
        ListCompanyUsersResponse,
        ListCompanyUsersArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/company/${queryArg.companyId}/users`,
          params: {
            all: queryArg.all,
            id__in: queryArg.idIn,
            id__not_in: queryArg.idNotIn,
            page_num: queryArg.pageNum,
            page_size: queryArg.pageSize,
            search: queryArg.search,
          },
        }),
        providesTags: ['Companies'],
      }),
      deactivateCompanyUser: build.mutation<
        DeactivateCompanyUserResponse,
        DeactivateCompanyUserArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/company/${queryArg.companyId}/users/${queryArg.userId}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Companies'],
      }),
      updateCompany: build.mutation<UpdateCompanyResponse, UpdateCompanyArg>({
        query: (queryArg) => ({
          url: `/api/internal/v1/company/${queryArg.companyId}`,
          method: 'PATCH',
          body: queryArg.updateCompanySerializer,
        }),
        invalidatesTags: ['Companies'],
      }),
      getFullUserRoles: build.query<
        GetFullUserRolesResponse,
        GetFullUserRolesArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/company/full-user-role`,
          params: {
            all: queryArg.all,
            id__in: queryArg.idIn,
            id__not_in: queryArg.idNotIn,
            page_num: queryArg.pageNum,
            page_size: queryArg.pageSize,
            active_status__in: queryArg.activeStatusIn,
            search: queryArg.search,
          },
        }),
        providesTags: ['Companies'],
      }),
      countUserRoles: build.query<CountUserRolesResponse, CountUserRolesArg>({
        query: (queryArg) => ({
          url: `/api/internal/v1/company/full-user-role/count`,
          params: {
            all: queryArg.all,
            id__in: queryArg.idIn,
            id__not_in: queryArg.idNotIn,
            page_num: queryArg.pageNum,
            page_size: queryArg.pageSize,
            active_status__in: queryArg.activeStatusIn,
            search: queryArg.search,
          },
        }),
        providesTags: ['Companies'],
      }),
      getCompanyCheckList: build.query<
        GetCompanyCheckListResponse,
        GetCompanyCheckListArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/company/${queryArg}/check-list`,
        }),
        providesTags: ['Companies'],
      }),
      getCompaniesCheckList: build.query<
        GetCompaniesCheckListResponse,
        GetCompaniesCheckListArg
      >({
        query: () => ({ url: `/api/internal/v1/company/check-list` }),
        providesTags: ['Companies'],
      }),
      doneCompanyCheckListItem: build.mutation<
        DoneCompanyCheckListItemResponse,
        DoneCompanyCheckListItemArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/company/${queryArg.companyId}/check-list/${queryArg.itemType}/done`,
          method: 'PATCH',
        }),
        invalidatesTags: ['Companies'],
      }),
      createCompanyInvite: build.mutation<
        CreateCompanyInviteResponse,
        CreateCompanyInviteArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/company/invite`,
          method: 'POST',
          body: queryArg,
        }),
        invalidatesTags: ['Companies'],
      }),
      listEmployees: build.query<ListEmployeesResponse, ListEmployeesArg>({
        query: (queryArg) => ({
          url: `/api/internal/v1/employee`,
          params: {
            all: queryArg.all,
            id__in: queryArg.idIn,
            id__not_in: queryArg.idNotIn,
            page_num: queryArg.pageNum,
            page_size: queryArg.pageSize,
            ordering: queryArg.ordering,
            company__in: queryArg.companyIn,
            status__in: queryArg.statusIn,
            group__in: queryArg.groupIn,
            leader__in: queryArg.leaderIn,
            sign_type__in: queryArg.signTypeIn,
            ipn__in: queryArg.ipnIn,
            position__in: queryArg.positionIn,
            phone: queryArg.phone,
            search: queryArg.search,
          },
        }),
        providesTags: ['Employee'],
      }),
      createEmployee: build.mutation<CreateEmployeeResponse, CreateEmployeeArg>(
        {
          query: (queryArg) => ({
            url: `/api/internal/v1/employee`,
            method: 'POST',
            body: queryArg,
          }),
          invalidatesTags: ['Employee'],
        },
      ),
      deleteEmployees: build.mutation<
        DeleteEmployeesResponse,
        DeleteEmployeesArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/employee`,
          method: 'DELETE',
          params: {
            all: queryArg.all,
            id__in: queryArg.idIn,
            id__not_in: queryArg.idNotIn,
            company__in: queryArg.companyIn,
            status__in: queryArg.statusIn,
            group__in: queryArg.groupIn,
            leader__in: queryArg.leaderIn,
            sign_type__in: queryArg.signTypeIn,
            ipn__in: queryArg.ipnIn,
            position__in: queryArg.positionIn,
            phone: queryArg.phone,
            search: queryArg.search,
          },
        }),
        invalidatesTags: ['Employee'],
      }),
      getEmployeesFilters: build.query<
        GetEmployeesFiltersResponse,
        GetEmployeesFiltersArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/employee/filters`,
          params: {
            all: queryArg.all,
            id__in: queryArg.idIn,
            id__not_in: queryArg.idNotIn,
            page_num: queryArg.pageNum,
            page_size: queryArg.pageSize,
            ordering: queryArg.ordering,
            company__in: queryArg.companyIn,
            status__in: queryArg.statusIn,
            group__in: queryArg.groupIn,
            leader__in: queryArg.leaderIn,
            sign_type__in: queryArg.signTypeIn,
            ipn__in: queryArg.ipnIn,
            position__in: queryArg.positionIn,
            phone: queryArg.phone,
            search: queryArg.search,
          },
        }),
        providesTags: ['Employee'],
      }),
      countEmployees: build.query<CountEmployeesResponse, CountEmployeesArg>({
        query: (queryArg) => ({
          url: `/api/internal/v1/employee/count`,
          params: {
            all: queryArg.all,
            id__in: queryArg.idIn,
            id__not_in: queryArg.idNotIn,
            company__in: queryArg.companyIn,
            status__in: queryArg.statusIn,
            group__in: queryArg.groupIn,
            leader__in: queryArg.leaderIn,
            sign_type__in: queryArg.signTypeIn,
            ipn__in: queryArg.ipnIn,
            position__in: queryArg.positionIn,
            phone: queryArg.phone,
            search: queryArg.search,
          },
        }),
        providesTags: ['Employee'],
      }),
      validateEmployee: build.mutation<
        ValidateEmployeeResponse,
        ValidateEmployeeArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/employee/validate`,
          method: 'POST',
          body: queryArg,
        }),
        invalidatesTags: ['Employee'],
      }),
      retrieveEmployee: build.query<
        RetrieveEmployeeResponse,
        RetrieveEmployeeArg
      >({
        query: (queryArg) => ({ url: `/api/internal/v1/employee/${queryArg}` }),
        providesTags: ['Employee'],
      }),
      updateEmployee: build.mutation<UpdateEmployeeResponse, UpdateEmployeeArg>(
        {
          query: (queryArg) => ({
            url: `/api/internal/v1/employee/${queryArg.employeeId}`,
            method: 'PUT',
            body: queryArg.employeeUpdateSerializer,
          }),
          invalidatesTags: ['Employee'],
        },
      ),
      partialUpdateEmployee: build.mutation<
        PartialUpdateEmployeeResponse,
        PartialUpdateEmployeeArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/employee/${queryArg.employeeId}`,
          method: 'PATCH',
          body: queryArg.partialEmployeeUpdateSerializer,
        }),
        invalidatesTags: ['Employee'],
      }),
      deleteEmployee: build.mutation<DeleteEmployeeResponse, DeleteEmployeeArg>(
        {
          query: (queryArg) => ({
            url: `/api/internal/v1/employee/${queryArg}`,
            method: 'DELETE',
          }),
          invalidatesTags: ['Employee'],
        },
      ),
      inviteEmployees: build.mutation<
        InviteEmployeesResponse,
        InviteEmployeesArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/employee/invite`,
          method: 'POST',
          params: {
            all: queryArg.all,
            id__in: queryArg.idIn,
            id__not_in: queryArg.idNotIn,
            company__in: queryArg.companyIn,
            status__in: queryArg.statusIn,
            group__in: queryArg.groupIn,
            leader__in: queryArg.leaderIn,
            sign_type__in: queryArg.signTypeIn,
            ipn__in: queryArg.ipnIn,
            position__in: queryArg.positionIn,
            phone: queryArg.phone,
            search: queryArg.search,
          },
        }),
        invalidatesTags: ['Employee'],
      }),
      updateEmployeePhoto: build.mutation<
        UpdateEmployeePhotoResponse,
        UpdateEmployeePhotoArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/employee/${queryArg.employeeId}/photo`,
          method: 'POST',
          body: queryArg.bodyUpdateEmployeePhoto,
        }),
        invalidatesTags: ['Employee'],
      }),
      createEmployeeAbsences: build.mutation<
        CreateEmployeeAbsencesResponse,
        CreateEmployeeAbsencesArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/employee/${queryArg.employeeId}/absences`,
          method: 'POST',
          body: queryArg.createAbsenceSerializer,
        }),
        invalidatesTags: ['Employee'],
      }),
      listEmployeeAbsences: build.query<
        ListEmployeeAbsencesResponse,
        ListEmployeeAbsencesArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/employee/${queryArg.employeeId}/absences`,
          params: {
            page_num: queryArg.pageNum,
            page_size: queryArg.pageSize,
          },
        }),
        providesTags: ['Employee'],
      }),
      updateEmployeeAbsences: build.mutation<
        UpdateEmployeeAbsencesResponse,
        UpdateEmployeeAbsencesArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/employee/${queryArg.employeeId}/absences/${queryArg.absenceId}`,
          method: 'PATCH',
          body: queryArg.updateAbsenceSerializer,
        }),
        invalidatesTags: ['Employee'],
      }),
      updateEmployeeWorkingPeriod: build.mutation<
        UpdateEmployeeWorkingPeriodResponse,
        UpdateEmployeeWorkingPeriodArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/employee/${queryArg.employeeId}/working-period/${queryArg.periodId}`,
          method: 'PATCH',
          body: queryArg.updateWorkingPeriodSerializer,
        }),
        invalidatesTags: ['Employee'],
      }),
      employeeUploadAttachment: build.mutation<
        EmployeeUploadAttachmentResponse,
        EmployeeUploadAttachmentArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/employee/${queryArg.employeeId}/attachment`,
          method: 'POST',
          body: queryArg.bodyEmployeeUploadAttachment,
        }),
        invalidatesTags: ['Employee'],
      }),
      employeeListAttachment: build.query<
        EmployeeListAttachmentResponse,
        EmployeeListAttachmentArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/employee/${queryArg.employeeId}/attachment`,
          params: {
            all: queryArg.all,
            id__in: queryArg.idIn,
            id__not_in: queryArg.idNotIn,
            page_num: queryArg.pageNum,
            page_size: queryArg.pageSize,
            task_id__in: queryArg.taskIdIn,
            attachment_type__in: queryArg.attachmentTypeIn,
          },
        }),
        providesTags: ['Employee'],
      }),
      inviteEmployee: build.mutation<InviteEmployeeResponse, InviteEmployeeArg>(
        {
          query: (queryArg) => ({
            url: `/api/internal/v1/employee/${queryArg}/invite`,
            method: 'POST',
          }),
          invalidatesTags: ['Employee'],
        },
      ),
      restoreEmployee: build.mutation<
        RestoreEmployeeResponse,
        RestoreEmployeeArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/employee/${queryArg}/restore`,
          method: 'POST',
        }),
        invalidatesTags: ['Employee'],
      }),
      changeEmployeeSigningType: build.mutation<
        ChangeEmployeeSigningTypeResponse,
        ChangeEmployeeSigningTypeArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/employee/${queryArg.employeeId}/change-signing-type`,
          method: 'POST',
          body: queryArg.updateSigningTypeSerializer,
        }),
        invalidatesTags: ['Employee'],
      }),
      listEmployeeDocuments: build.query<
        ListEmployeeDocumentsResponse,
        ListEmployeeDocumentsArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/employee/${queryArg.employeeId}/documents`,
          params: {
            all: queryArg.all,
            id__in: queryArg.idIn,
            id__not_in: queryArg.idNotIn,
            page_num: queryArg.pageNum,
            page_size: queryArg.pageSize,
            ordering: queryArg.ordering,
            document_type__in: queryArg.documentTypeIn,
          },
        }),
        providesTags: ['Employee'],
      }),
      createGroup: build.mutation<CreateGroupResponse, CreateGroupArg>({
        query: (queryArg) => ({
          url: `/api/internal/v1/group`,
          method: 'POST',
          body: queryArg,
        }),
        invalidatesTags: ['Group'],
      }),
      listGroups: build.query<ListGroupsResponse, ListGroupsArg>({
        query: (queryArg) => ({
          url: `/api/internal/v1/group`,
          params: {
            page_num: queryArg.pageNum,
            page_size: queryArg.pageSize,
            company_id: queryArg.companyId,
            search: queryArg.search,
          },
        }),
        providesTags: ['Group'],
      }),
      updateGroup: build.mutation<UpdateGroupResponse, UpdateGroupArg>({
        query: (queryArg) => ({
          url: `/api/internal/v1/group/${queryArg.groupId}`,
          method: 'PUT',
          body: queryArg.groupUpdateSerializer,
        }),
        invalidatesTags: ['Group'],
      }),
      createFileImportUpload: build.mutation<
        CreateFileImportUploadResponse,
        CreateFileImportUploadArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/file-import`,
          method: 'POST',
          body: queryArg.bodyCreateFileImportUpload,
          params: {
            import_type: queryArg.importType,
            company_id: queryArg.companyId,
          },
        }),
        invalidatesTags: ['File Import'],
      }),
      getFileImportUpload: build.query<
        GetFileImportUploadResponse,
        GetFileImportUploadArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/file-import/${queryArg}`,
        }),
        providesTags: ['File Import'],
      }),
      createVacationTaskTemplate: build.mutation<
        CreateVacationTaskTemplateResponse,
        CreateVacationTaskTemplateArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/task-template/vacation`,
          method: 'POST',
          body: queryArg,
        }),
        invalidatesTags: ['Task Template'],
      }),
      generateTaskTemplateDocumentPreview: build.mutation<
        GenerateTaskTemplateDocumentPreviewResponse,
        GenerateTaskTemplateDocumentPreviewArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/task-template/vacation/document/preview`,
          method: 'POST',
          body: queryArg,
        }),
        invalidatesTags: ['Task Template'],
      }),
      retrieveVacationTaskTemplate: build.query<
        RetrieveVacationTaskTemplateResponse,
        RetrieveVacationTaskTemplateArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/task-template/vacation/${queryArg}`,
        }),
        providesTags: ['Task Template'],
      }),
      updateVacationTaskTemplate: build.mutation<
        UpdateVacationTaskTemplateResponse,
        UpdateVacationTaskTemplateArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/task-template/vacation/${queryArg.taskVersionIdentifier}`,
          method: 'PUT',
          body: queryArg.vacationTaskTemplateUpdateSerializer,
        }),
        invalidatesTags: ['Task Template'],
      }),
      updateVacationTaskTemplateManager: build.mutation<
        UpdateVacationTaskTemplateManagerResponse,
        UpdateVacationTaskTemplateManagerArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/task-template/vacation/${queryArg.taskVersionIdentifier}/update-manager`,
          method: 'PATCH',
          body: queryArg.vacationTaskTemplateManagerSerializer,
        }),
        invalidatesTags: ['Task Template'],
      }),
      getTemplatePeriods: build.query<
        GetTemplatePeriodsResponse,
        GetTemplatePeriodsArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/task-template/vacation/${queryArg.taskVersionIdentifier}/${queryArg.employeeId}/periods`,
        }),
        providesTags: ['Task Template'],
      }),
      listTaskTemplates: build.query<
        ListTaskTemplatesResponse,
        ListTaskTemplatesArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/task-template`,
          params: {
            all: queryArg.all,
            id__in: queryArg.idIn,
            id__not_in: queryArg.idNotIn,
            page_num: queryArg.pageNum,
            page_size: queryArg.pageSize,
            company__in: queryArg.companyIn,
            status__in: queryArg.statusIn,
            task_type__in: queryArg.taskTypeIn,
            task_type__not_in: queryArg.taskTypeNotIn,
            search: queryArg.search,
            is_onboarding: queryArg.isOnboarding,
          },
        }),
        providesTags: ['Task Template'],
      }),
      listTaskTemplatesByBatch: build.query<
        ListTaskTemplatesByBatchResponse,
        ListTaskTemplatesByBatchArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/task-template/batch/${queryArg.taskTemplateBatch}`,
          params: {
            all: queryArg.all,
            id__in: queryArg.idIn,
            id__not_in: queryArg.idNotIn,
            page_num: queryArg.pageNum,
            page_size: queryArg.pageSize,
            company__in: queryArg.companyIn,
            status__in: queryArg.statusIn,
            task_type__in: queryArg.taskTypeIn,
            task_type__not_in: queryArg.taskTypeNotIn,
            search: queryArg.search,
            is_onboarding: queryArg.isOnboarding,
          },
        }),
        providesTags: ['Task Template'],
      }),
      toggleActiveTaskTemplates: build.mutation<
        ToggleActiveTaskTemplatesResponse,
        ToggleActiveTaskTemplatesArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/task-template/${queryArg}/toggle-active`,
          method: 'PUT',
        }),
        invalidatesTags: ['Task Template'],
      }),
      countTasks: build.query<CountTasksResponse, CountTasksArg>({
        query: (queryArg) => ({
          url: `/api/internal/v1/task/count`,
          params: {
            all: queryArg.all,
            id__in: queryArg.idIn,
            id__not_in: queryArg.idNotIn,
            page_num: queryArg.pageNum,
            page_size: queryArg.pageSize,
            company__in: queryArg.companyIn,
            task_template__in: queryArg.taskTemplateIn,
            status__in: queryArg.statusIn,
            start_date: queryArg.startDate,
            end_date: queryArg.endDate,
            manager__in: queryArg.managerIn,
            search: queryArg.search,
            require_attention: queryArg.requireAttention,
          },
        }),
        providesTags: ['Task'],
      }),
      getTaskFilters: build.query<GetTaskFiltersResponse, GetTaskFiltersArg>({
        query: (queryArg) => ({
          url: `/api/internal/v1/task/filters`,
          params: {
            all: queryArg.all,
            id__in: queryArg.idIn,
            id__not_in: queryArg.idNotIn,
            page_num: queryArg.pageNum,
            page_size: queryArg.pageSize,
            company__in: queryArg.companyIn,
            task_template__in: queryArg.taskTemplateIn,
            status__in: queryArg.statusIn,
            start_date: queryArg.startDate,
            end_date: queryArg.endDate,
            manager__in: queryArg.managerIn,
            search: queryArg.search,
            require_attention: queryArg.requireAttention,
          },
        }),
        providesTags: ['Task'],
      }),
      getTaskDocumentPreview: build.query<
        GetTaskDocumentPreviewResponse,
        GetTaskDocumentPreviewArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/task/${queryArg.taskId}/document/${queryArg.documentId}/preview`,
        }),
        providesTags: ['Task'],
      }),
      deleteTaskDocument: build.mutation<
        DeleteTaskDocumentResponse,
        DeleteTaskDocumentArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/task/${queryArg.taskId}/document/${queryArg.documentId}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Task'],
      }),
      taskUploadDocument: build.mutation<
        TaskUploadDocumentResponse,
        TaskUploadDocumentArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/task/${queryArg.taskId}/document`,
          method: 'POST',
          body: queryArg.bodyTaskUploadDocument,
        }),
        invalidatesTags: ['Task'],
      }),
      listTaskDocuments: build.query<
        ListTaskDocumentsResponse,
        ListTaskDocumentsArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/task/${queryArg}/document`,
        }),
        providesTags: ['Task'],
      }),
      getTaskOperations: build.query<
        GetTaskOperationsResponse,
        GetTaskOperationsArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/task/${queryArg}/operations`,
        }),
        providesTags: ['Task'],
      }),
      getTaskErrors: build.query<GetTaskErrorsResponse, GetTaskErrorsArg>({
        query: (queryArg) => ({
          url: `/api/internal/v1/task/${queryArg}/errors`,
        }),
        providesTags: ['Task'],
      }),
      createTask: build.mutation<CreateTaskResponse, CreateTaskArg>({
        query: (queryArg) => ({
          url: `/api/internal/v1/task`,
          method: 'POST',
          body: queryArg,
        }),
        invalidatesTags: ['Task'],
      }),
      listTasks: build.query<ListTasksResponse, ListTasksArg>({
        query: (queryArg) => ({
          url: `/api/internal/v1/task`,
          params: {
            all: queryArg.all,
            id__in: queryArg.idIn,
            id__not_in: queryArg.idNotIn,
            page_num: queryArg.pageNum,
            page_size: queryArg.pageSize,
            company__in: queryArg.companyIn,
            task_template__in: queryArg.taskTemplateIn,
            status__in: queryArg.statusIn,
            start_date: queryArg.startDate,
            end_date: queryArg.endDate,
            manager__in: queryArg.managerIn,
            search: queryArg.search,
            require_attention: queryArg.requireAttention,
          },
        }),
        providesTags: ['Task'],
      }),
      deleteTask: build.mutation<DeleteTaskResponse, DeleteTaskArg>({
        query: (queryArg) => ({
          url: `/api/internal/v1/task/${queryArg}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Task'],
      }),
      retrieveTask: build.query<RetrieveTaskResponse, RetrieveTaskArg>({
        query: (queryArg) => ({ url: `/api/internal/v1/task/${queryArg}` }),
        providesTags: ['Task'],
      }),
      updateTask: build.mutation<UpdateTaskResponse, UpdateTaskArg>({
        query: (queryArg) => ({
          url: `/api/internal/v1/task/${queryArg.taskId}`,
          method: 'PUT',
          body: queryArg.taskUpdateSerializer,
        }),
        invalidatesTags: ['Task'],
      }),
      cancelTask: build.mutation<CancelTaskResponse, CancelTaskArg>({
        query: (queryArg) => ({
          url: `/api/internal/v1/task/${queryArg.taskId}/cancel`,
          method: 'POST',
          body: queryArg.taskCancelSerializer,
        }),
        invalidatesTags: ['Task'],
      }),
      sendDocuments: build.mutation<SendDocumentsResponse, SendDocumentsArg>({
        query: (queryArg) => ({
          url: `/api/internal/v1/task/${queryArg.taskId}/send-documents`,
          method: 'POST',
          body: queryArg.sendTaskDocumentsSerializer,
        }),
        invalidatesTags: ['Task'],
      }),
      getTaskPeriods: build.query<GetTaskPeriodsResponse, GetTaskPeriodsArg>({
        query: (queryArg) => ({
          url: `/api/internal/v1/task/${queryArg}/periods`,
        }),
        providesTags: ['Task'],
      }),
      taskUploadAttachment: build.mutation<
        TaskUploadAttachmentResponse,
        TaskUploadAttachmentArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/task/${queryArg.taskId}/attachment`,
          method: 'POST',
          body: queryArg.bodyTaskUploadAttachment,
        }),
        invalidatesTags: ['Task'],
      }),
      taskFullRouteEdit: build.mutation<
        TaskFullRouteEditResponse,
        TaskFullRouteEditArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/task/${queryArg.taskId}/route`,
          method: 'PUT',
          body: queryArg.taskFullRouteUpdateSerializer,
        }),
        invalidatesTags: ['Task'],
      }),
      taskRouteDelete: build.mutation<
        TaskRouteDeleteResponse,
        TaskRouteDeleteArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/task/${queryArg.taskId}/route/${queryArg.routeId}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Task'],
      }),
      taskRouteEdit: build.mutation<TaskRouteEditResponse, TaskRouteEditArg>({
        query: (queryArg) => ({
          url: `/api/internal/v1/task/${queryArg.taskId}/route/${queryArg.routeId}/actions`,
          method: 'PUT',
          body: queryArg.taskRouteUpdateSerializer,
        }),
        invalidatesTags: ['Task'],
      }),
      taskRouteDocumentPreview: build.query<
        TaskRouteDocumentPreviewResponse,
        TaskRouteDocumentPreviewArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/task/${queryArg.taskId}/route/${queryArg.routeId}/preview`,
        }),
        providesTags: ['Task'],
      }),
      taskRouteDocumentPreviewWithParameters: build.mutation<
        TaskRouteDocumentPreviewWithParametersResponse,
        TaskRouteDocumentPreviewWithParametersArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/task/${queryArg.taskId}/route/${queryArg.routeId}/preview`,
          method: 'POST',
          body: queryArg.sendTaskDocumentSerializer,
        }),
        invalidatesTags: ['Task'],
      }),
      taskRouteActionRemindToAction: build.mutation<
        TaskRouteActionRemindToActionResponse,
        TaskRouteActionRemindToActionArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/task/${queryArg.taskId}/route/${queryArg.routeId}/action/${queryArg.actionId}/remind`,
          method: 'POST',
        }),
        invalidatesTags: ['Task'],
      }),
      getUploadedFile: build.query<GetUploadedFileResponse, GetUploadedFileArg>(
        {
          query: (queryArg) => ({
            url: `/api/internal/v1/files/key/${queryArg}`,
          }),
          providesTags: ['Files'],
        },
      ),
      getUploadedFileById: build.query<
        GetUploadedFileByIdResponse,
        GetUploadedFileByIdArg
      >({
        query: (queryArg) => ({ url: `/api/internal/v1/files/${queryArg}` }),
        providesTags: ['Files'],
      }),
      createPublicToken: build.mutation<
        CreatePublicTokenResponse,
        CreatePublicTokenArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/public-api/token`,
          method: 'POST',
          body: queryArg,
        }),
        invalidatesTags: ['PublicAPI'],
      }),
      listCompanyPublicTokens: build.query<
        ListCompanyPublicTokensResponse,
        ListCompanyPublicTokensArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/public-api/company/${queryArg}/tokens`,
        }),
        providesTags: ['PublicAPI'],
      }),
      updatePublicToken: build.mutation<
        UpdatePublicTokenResponse,
        UpdatePublicTokenArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/public-api/token/${queryArg.tokenId}`,
          method: 'PATCH',
          body: queryArg.updatePublicApiTokenSerializer,
        }),
        invalidatesTags: ['PublicAPI'],
      }),
      deletePublicToken: build.mutation<
        DeletePublicTokenResponse,
        DeletePublicTokenArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/public-api/token/${queryArg}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['PublicAPI'],
      }),
      createOnboardingAttempt: build.mutation<
        CreateOnboardingAttemptResponse,
        CreateOnboardingAttemptArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/employee-onboarding/attempt`,
          method: 'POST',
          body: queryArg,
        }),
        invalidatesTags: ['Employee Onboarding'],
      }),
      updateOnboardingAttempt: build.mutation<
        UpdateOnboardingAttemptResponse,
        UpdateOnboardingAttemptArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/employee-onboarding/attempt/${queryArg.attemptId}`,
          method: 'PATCH',
          body: queryArg.updateEmployeeOnboardingAttemptSerializer,
        }),
        invalidatesTags: ['Employee Onboarding'],
      }),
      getDiiaOnboardingDeeplink: build.query<
        GetDiiaOnboardingDeeplinkResponse,
        GetDiiaOnboardingDeeplinkArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/employee-onboarding/attempt/${queryArg}/deeplink`,
        }),
        providesTags: ['Employee Onboarding'],
      }),
      vchasnoMobileEmployees: build.query<
        VchasnoMobileEmployeesResponse,
        VchasnoMobileEmployeesArg
      >({
        query: (queryArg) => ({
          url: `/api/mobile/v1/${queryArg.vchasnoId}/employee`,
          params: {
            all: queryArg.all,
            id__in: queryArg.idIn,
            id__not_in: queryArg.idNotIn,
            ordering: queryArg.ordering,
            status__in: queryArg.statusIn,
          },
        }),
        providesTags: ['Mobile'],
      }),
      vchasnoMobileEmployeeById: build.query<
        VchasnoMobileEmployeeByIdResponse,
        VchasnoMobileEmployeeByIdArg
      >({
        query: (queryArg) => ({
          url: `/api/mobile/v1/${queryArg.vchasnoId}/employee/${queryArg.employeeId}`,
        }),
        providesTags: ['Mobile'],
      }),
      vchasnoMobileEmployeeTaskTemplate: build.query<
        VchasnoMobileEmployeeTaskTemplateResponse,
        VchasnoMobileEmployeeTaskTemplateArg
      >({
        query: (queryArg) => ({
          url: `/api/mobile/v1/${queryArg.vchasnoId}/${queryArg.employeeId}/task/task-template`,
          params: {
            all: queryArg.all,
            id__in: queryArg.idIn,
            id__not_in: queryArg.idNotIn,
            page_num: queryArg.pageNum,
            page_size: queryArg.pageSize,
            status__in: queryArg.statusIn,
            search: queryArg.search,
            task_type__in: queryArg.taskTypeIn,
          },
        }),
        providesTags: ['Mobile'],
      }),
      vchasnoMobileEmployeeTaskCreate: build.mutation<
        VchasnoMobileEmployeeTaskCreateResponse,
        VchasnoMobileEmployeeTaskCreateArg
      >({
        query: (queryArg) => ({
          url: `/api/mobile/v1/${queryArg.vchasnoId}/${queryArg.employeeId}/task/`,
          method: 'POST',
          body: queryArg.employeeTaskCreateSerializer,
        }),
        invalidatesTags: ['Mobile'],
      }),
      vchasnoMobileEmployeeTaskUpdate: build.mutation<
        VchasnoMobileEmployeeTaskUpdateResponse,
        VchasnoMobileEmployeeTaskUpdateArg
      >({
        query: (queryArg) => ({
          url: `/api/mobile/v1/${queryArg.vchasnoId}/${queryArg.employeeId}/task/${queryArg.taskId}/`,
          method: 'PATCH',
          body: queryArg.employeeTaskUpdateSerializer,
        }),
        invalidatesTags: ['Mobile'],
      }),
      vchasnoMobileEmployeeTask: build.query<
        VchasnoMobileEmployeeTaskResponse,
        VchasnoMobileEmployeeTaskArg
      >({
        query: (queryArg) => ({
          url: `/api/mobile/v1/${queryArg.vchasnoId}/${queryArg.employeeId}/task`,
          params: {
            all: queryArg.all,
            id__in: queryArg.idIn,
            id__not_in: queryArg.idNotIn,
            page_num: queryArg.pageNum,
            page_size: queryArg.pageSize,
            status__in: queryArg.statusIn,
            search: queryArg.search,
            route_status__in: queryArg.routeStatusIn,
          },
        }),
        providesTags: ['Mobile'],
      }),
      vchasnoMobileEmployeeTaskToAction: build.query<
        VchasnoMobileEmployeeTaskToActionResponse,
        VchasnoMobileEmployeeTaskToActionArg
      >({
        query: (queryArg) => ({
          url: `/api/mobile/v1/${queryArg.vchasnoId}/${queryArg.employeeId}/task/to-action`,
          params: {
            all: queryArg.all,
            id__in: queryArg.idIn,
            id__not_in: queryArg.idNotIn,
            page_num: queryArg.pageNum,
            page_size: queryArg.pageSize,
            status__in: queryArg.statusIn,
            search: queryArg.search,
            route_status__in: queryArg.routeStatusIn,
          },
        }),
        providesTags: ['Mobile'],
      }),
      vchasnoMobileEmployeeTaskById: build.query<
        VchasnoMobileEmployeeTaskByIdResponse,
        VchasnoMobileEmployeeTaskByIdArg
      >({
        query: (queryArg) => ({
          url: `/api/mobile/v1/${queryArg.vchasnoId}/${queryArg.employeeId}/task/${queryArg.taskId}`,
        }),
        providesTags: ['Mobile'],
      }),
      vchasnoMobileEmployeeTaskToActionCount: build.query<
        VchasnoMobileEmployeeTaskToActionCountResponse,
        VchasnoMobileEmployeeTaskToActionCountArg
      >({
        query: (queryArg) => ({
          url: `/api/mobile/v1/${queryArg.vchasnoId}/${queryArg.employeeId}/task/to-action/count`,
          params: {
            all: queryArg.all,
            id__in: queryArg.idIn,
            id__not_in: queryArg.idNotIn,
            page_num: queryArg.pageNum,
            page_size: queryArg.pageSize,
            status__in: queryArg.statusIn,
            search: queryArg.search,
            route_status__in: queryArg.routeStatusIn,
          },
        }),
        providesTags: ['Mobile'],
      }),
      vchasnoMobileCancelTask: build.mutation<
        VchasnoMobileCancelTaskResponse,
        VchasnoMobileCancelTaskArg
      >({
        query: (queryArg) => ({
          url: `/api/mobile/v1/${queryArg.vchasnoId}/${queryArg.employeeId}/task/${queryArg.taskId}/cancel/`,
          method: 'POST',
          body: queryArg.taskCancelSerializer,
        }),
        invalidatesTags: ['Mobile'],
      }),
      vchasnoMobileApproveTask: build.mutation<
        VchasnoMobileApproveTaskResponse,
        VchasnoMobileApproveTaskArg
      >({
        query: (queryArg) => ({
          url: `/api/mobile/v1/${queryArg.vchasnoId}/${queryArg.employeeId}/task/${queryArg.taskId}/${queryArg.actionStatus}/`,
          method: 'POST',
          body: queryArg.body,
        }),
        invalidatesTags: ['Mobile'],
      }),
      mobileGetUploadedFile: build.query<
        MobileGetUploadedFileResponse,
        MobileGetUploadedFileArg
      >({
        query: (queryArg) => ({
          url: `/api/mobile/v1/${queryArg.vchasnoId}/${queryArg.employeeId}/file/key/${queryArg.fileKey}`,
        }),
        providesTags: ['Mobile'],
      }),
      updateCompanyMaxEmployees: build.mutation<
        UpdateCompanyMaxEmployeesResponse,
        UpdateCompanyMaxEmployeesArg
      >({
        query: (queryArg) => ({
          url: `/api/admin/v1/company/${queryArg.companyId}/max-employees`,
          method: 'PATCH',
          body: queryArg.companyMaxEmployeesUpdateSerializer,
        }),
        invalidatesTags: ['Admin'],
      }),
      listCompanyAdmin: build.query<
        ListCompanyAdminResponse,
        ListCompanyAdminArg
      >({
        query: (queryArg) => ({
          url: `/api/admin/v1/company`,
          params: {
            all: queryArg.all,
            id__in: queryArg.idIn,
            id__not_in: queryArg.idNotIn,
            page_num: queryArg.pageNum,
            page_size: queryArg.pageSize,
            search: queryArg.search,
            plan__in: queryArg.planIn,
          },
        }),
        providesTags: ['Admin'],
      }),
      changeCompanyPlan: build.mutation<
        ChangeCompanyPlanResponse,
        ChangeCompanyPlanArg
      >({
        query: (queryArg) => ({
          url: `/api/admin/v1/company/${queryArg.companyId}/plan`,
          method: 'PATCH',
          body: queryArg.changePlanSerializer,
        }),
        invalidatesTags: ['Admin'],
      }),
      resyncCompanyTemplates: build.mutation<
        ResyncCompanyTemplatesResponse,
        ResyncCompanyTemplatesArg
      >({
        query: (queryArg) => ({
          url: `/api/admin/v1/company/${queryArg}/resync-templates`,
          method: 'PATCH',
        }),
        invalidatesTags: ['Admin'],
      }),
      updateUserInternalRole: build.mutation<
        UpdateUserInternalRoleResponse,
        UpdateUserInternalRoleArg
      >({
        query: (queryArg) => ({
          url: `/api/admin/v1/user/${queryArg.userId}/role`,
          method: 'PATCH',
          body: queryArg.updateInternalUserRoleSerializer,
        }),
        invalidatesTags: ['Admin'],
      }),
      adminAddEdoRole: build.mutation<
        AdminAddEdoRoleResponse,
        AdminAddEdoRoleArg
      >({
        query: (queryArg) => ({
          url: `/api/admin/v1/user/add-edo-role`,
          method: 'POST',
          body: queryArg,
        }),
        invalidatesTags: ['Admin'],
      }),
      adminRemoveEdoRole: build.mutation<
        AdminRemoveEdoRoleResponse,
        AdminRemoveEdoRoleArg
      >({
        query: (queryArg) => ({
          url: `/api/admin/v1/user/remove-edo-role`,
          method: 'POST',
          body: queryArg,
        }),
        invalidatesTags: ['Admin'],
      }),
      adminInviteEmployee: build.mutation<
        AdminInviteEmployeeResponse,
        AdminInviteEmployeeArg
      >({
        query: (queryArg) => ({
          url: `/api/admin/v1/employee/${queryArg}/invite`,
          method: 'PATCH',
        }),
        invalidatesTags: ['Admin'],
      }),
      createPublicApiToken: build.mutation<
        CreatePublicApiTokenResponse,
        CreatePublicApiTokenArg
      >({
        query: (queryArg) => ({
          url: `/api/admin/v1/public-api-token`,
          method: 'POST',
          body: queryArg,
        }),
        invalidatesTags: ['Admin'],
      }),
      updatePublicApiToken: build.mutation<
        UpdatePublicApiTokenResponse,
        UpdatePublicApiTokenArg
      >({
        query: (queryArg) => ({
          url: `/api/admin/v1/public-api-token/${queryArg.tokenId}`,
          method: 'PATCH',
          body: queryArg.updatePublicApiTokenSerializer,
        }),
        invalidatesTags: ['Admin'],
      }),
      adminUploadTaskTemplateDocument: build.mutation<
        AdminUploadTaskTemplateDocumentResponse,
        AdminUploadTaskTemplateDocumentArg
      >({
        query: (queryArg) => ({
          url: `/api/admin/v1/task-template/document/${queryArg.documentId}/upload`,
          method: 'POST',
          body: queryArg.bodyAdminUploadTaskTemplateDocument,
        }),
        invalidatesTags: ['Admin'],
      }),
      adminSetTaskTemplateDocumentGenerationParams: build.mutation<
        AdminSetTaskTemplateDocumentGenerationParamsResponse,
        AdminSetTaskTemplateDocumentGenerationParamsArg
      >({
        query: (queryArg) => ({
          url: `/api/admin/v1/task-template/document/${queryArg.documentId}/set-params`,
          method: 'PUT',
          body: queryArg.taskDocumentGenerationParamsSerializerInput,
        }),
        invalidatesTags: ['Admin'],
      }),
      publicCreateEmployee: build.mutation<
        PublicCreateEmployeeResponse,
        PublicCreateEmployeeArg
      >({
        query: (queryArg) => ({
          url: `/api/public/v1/employee`,
          method: 'POST',
          body: queryArg,
        }),
        invalidatesTags: ['Public API Employee'],
      }),
      publicListEmployees: build.query<
        PublicListEmployeesResponse,
        PublicListEmployeesArg
      >({
        query: (queryArg) => ({
          url: `/api/public/v1/employee`,
          params: {
            all: queryArg.all,
            id__in: queryArg.idIn,
            id__not_in: queryArg.idNotIn,
            page_num: queryArg.pageNum,
            page_size: queryArg.pageSize,
            ordering: queryArg.ordering,
            status__in: queryArg.statusIn,
            group__in: queryArg.groupIn,
            leader__in: queryArg.leaderIn,
            sign_type__in: queryArg.signTypeIn,
            position__in: queryArg.positionIn,
          },
        }),
        providesTags: ['Public API Employee'],
      }),
      publicUpdateEmployee: build.mutation<
        PublicUpdateEmployeeResponse,
        PublicUpdateEmployeeArg
      >({
        query: (queryArg) => ({
          url: `/api/public/v1/employee/${queryArg.employeeId}`,
          method: 'PUT',
          body: queryArg.publicApiUpdateEmployeeSerializer,
        }),
        invalidatesTags: ['Public API Employee'],
      }),
      publicDeleteEmployee: build.mutation<
        PublicDeleteEmployeeResponse,
        PublicDeleteEmployeeArg
      >({
        query: (queryArg) => ({
          url: `/api/public/v1/employee/${queryArg}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Public API Employee'],
      }),
      publicGetEmployee: build.query<
        PublicGetEmployeeResponse,
        PublicGetEmployeeArg
      >({
        query: (queryArg) => ({ url: `/api/public/v1/employee/${queryArg}` }),
        providesTags: ['Public API Employee'],
      }),
      publicCreateEmployeeAbsences: build.mutation<
        PublicCreateEmployeeAbsencesResponse,
        PublicCreateEmployeeAbsencesArg
      >({
        query: (queryArg) => ({
          url: `/api/public/v1/employee/${queryArg.employeeId}/absences`,
          method: 'POST',
          body: queryArg.createAbsenceSerializer,
        }),
        invalidatesTags: ['Public API Employee'],
      }),
      publicListEmployeeAbsences: build.query<
        PublicListEmployeeAbsencesResponse,
        PublicListEmployeeAbsencesArg
      >({
        query: (queryArg) => ({
          url: `/api/public/v1/employee/${queryArg.employeeId}/absences`,
          params: {
            page_num: queryArg.pageNum,
            page_size: queryArg.pageSize,
          },
        }),
        providesTags: ['Public API Employee'],
      }),
      publicUpdateEmployeeAbsences: build.mutation<
        PublicUpdateEmployeeAbsencesResponse,
        PublicUpdateEmployeeAbsencesArg
      >({
        query: (queryArg) => ({
          url: `/api/public/v1/employee/${queryArg.employeeId}/absences/${queryArg.absenceId}`,
          method: 'PATCH',
          body: queryArg.updateAbsenceSerializer,
        }),
        invalidatesTags: ['Public API Employee'],
      }),
      publicListGroups: build.query<
        PublicListGroupsResponse,
        PublicListGroupsArg
      >({
        query: (queryArg) => ({
          url: `/api/public/v1/group`,
          params: {
            ordering: queryArg.ordering,
            all: queryArg.all,
            id__in: queryArg.idIn,
            id__not_in: queryArg.idNotIn,
            page_num: queryArg.pageNum,
            page_size: queryArg.pageSize,
            search: queryArg.search,
          },
        }),
        providesTags: ['Public API Group'],
      }),
      publicCreateGroup: build.mutation<
        PublicCreateGroupResponse,
        PublicCreateGroupArg
      >({
        query: (queryArg) => ({
          url: `/api/public/v1/group`,
          method: 'POST',
          body: queryArg,
        }),
        invalidatesTags: ['Public API Group'],
      }),
      publicGetGroup: build.query<PublicGetGroupResponse, PublicGetGroupArg>({
        query: (queryArg) => ({ url: `/api/public/v1/group/${queryArg}` }),
        providesTags: ['Public API Group'],
      }),
      publicUpdateGroup: build.mutation<
        PublicUpdateGroupResponse,
        PublicUpdateGroupArg
      >({
        query: (queryArg) => ({
          url: `/api/public/v1/group/${queryArg.groupId}`,
          method: 'PUT',
          body: queryArg.publicApiUpdateGroupSerializer,
        }),
        invalidatesTags: ['Public API Group'],
      }),
      publicDeleteGroup: build.mutation<
        PublicDeleteGroupResponse,
        PublicDeleteGroupArg
      >({
        query: (queryArg) => ({
          url: `/api/public/v1/group/${queryArg}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Public API Group'],
      }),
      publicListVacationDocuments: build.query<
        PublicListVacationDocumentsResponse,
        PublicListVacationDocumentsArg
      >({
        query: (queryArg) => ({
          url: `/api/public/v1/vacation/${queryArg}/document`,
        }),
        providesTags: ['Public API Vacation'],
      }),
      publicListVacationDocumentSigners: build.query<
        PublicListVacationDocumentSignersResponse,
        PublicListVacationDocumentSignersArg
      >({
        query: (queryArg) => ({
          url: `/api/public/v1/vacation/${queryArg.taskId}/document/${queryArg.documentId}/signer`,
        }),
        providesTags: ['Public API Vacation'],
      }),
      publicCreateVacationDocumentSigner: build.mutation<
        PublicCreateVacationDocumentSignerResponse,
        PublicCreateVacationDocumentSignerArg
      >({
        query: (queryArg) => ({
          url: `/api/public/v1/vacation/${queryArg.taskId}/document/${queryArg.documentId}/signer`,
          method: 'POST',
          body: queryArg.publicApiCreateDocumentSignerSerializer,
        }),
        invalidatesTags: ['Public API Vacation'],
      }),
      publicDeleteVacationDocumentSigner: build.mutation<
        PublicDeleteVacationDocumentSignerResponse,
        PublicDeleteVacationDocumentSignerArg
      >({
        query: (queryArg) => ({
          url: `/api/public/v1/vacation/${queryArg.taskId}/document/${queryArg.documentId}/signer/${queryArg.executorId}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Public API Vacation'],
      }),
      publicSendVacationDocuments: build.mutation<
        PublicSendVacationDocumentsResponse,
        PublicSendVacationDocumentsArg
      >({
        query: (queryArg) => ({
          url: `/api/public/v1/vacation/${queryArg.taskId}/send-documents`,
          method: 'POST',
          body: queryArg.sendVacationDocumentsSerializer,
        }),
        invalidatesTags: ['Public API Vacation'],
      }),
      publicListUniversalDocuments: build.query<
        PublicListUniversalDocumentsResponse,
        PublicListUniversalDocumentsArg
      >({
        query: (queryArg) => ({
          url: `/api/public/v1/universal-document`,
          params: {
            all: queryArg.all,
            id__in: queryArg.idIn,
            id__not_in: queryArg.idNotIn,
            page_num: queryArg.pageNum,
            page_size: queryArg.pageSize,
            company__in: queryArg.companyIn,
            task_template__in: queryArg.taskTemplateIn,
            status__in: queryArg.statusIn,
            start_date: queryArg.startDate,
            end_date: queryArg.endDate,
            manager__in: queryArg.managerIn,
            search: queryArg.search,
            require_attention: queryArg.requireAttention,
          },
        }),
        providesTags: ['Public API Universal Document'],
      }),
      publicListUniversalDocumentDocuments: build.query<
        PublicListUniversalDocumentDocumentsResponse,
        PublicListUniversalDocumentDocumentsArg
      >({
        query: (queryArg) => ({
          url: `/api/public/v1/universal-document/${queryArg}/document`,
        }),
        providesTags: ['Public API Universal Document'],
      }),
      publicListUniversalDocumentDocumentSigners: build.query<
        PublicListUniversalDocumentDocumentSignersResponse,
        PublicListUniversalDocumentDocumentSignersArg
      >({
        query: (queryArg) => ({
          url: `/api/public/v1/universal-document/${queryArg.taskId}/document/${queryArg.documentId}/signer`,
        }),
        providesTags: ['Public API Universal Document'],
      }),
      publicCreateUniversalDocumentDocumentSigner: build.mutation<
        PublicCreateUniversalDocumentDocumentSignerResponse,
        PublicCreateUniversalDocumentDocumentSignerArg
      >({
        query: (queryArg) => ({
          url: `/api/public/v1/universal-document/${queryArg.taskId}/document/${queryArg.documentId}/signer`,
          method: 'POST',
          body: queryArg.publicApiCreateDocumentSignerSerializer,
        }),
        invalidatesTags: ['Public API Universal Document'],
      }),
      publicDeleteUniversalDocumentDocumentSigner: build.mutation<
        PublicDeleteUniversalDocumentDocumentSignerResponse,
        PublicDeleteUniversalDocumentDocumentSignerArg
      >({
        query: (queryArg) => ({
          url: `/api/public/v1/universal-document/${queryArg.taskId}/document/${queryArg.documentId}/signer/${queryArg.executorId}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Public API Universal Document'],
      }),
      publicSendUniversalDocumentDocuments: build.mutation<
        PublicSendUniversalDocumentDocumentsResponse,
        PublicSendUniversalDocumentDocumentsArg
      >({
        query: (queryArg) => ({
          url: `/api/public/v1/universal-document/${queryArg.taskId}/send-documents`,
          method: 'POST',
          body: queryArg.sendUniversalDocumentsSerializer,
        }),
        invalidatesTags: ['Public API Universal Document'],
      }),
      handleFilesBatchUpload: build.mutation<
        HandleFilesBatchUploadResponse,
        HandleFilesBatchUploadArg
      >({
        query: (queryArg) => ({
          url: `/api/internal/v1/diia/files-batch-upload-endpoint`,
          method: 'POST',
          body: queryArg.bodyHandleFilesBatchUpload,
          headers: {
            'X-Document-Request-Trace-Id':
              queryArg['X-Document-Request-Trace-Id'],
          },
        }),
        invalidatesTags: ['Diia'],
      }),
      getEmployeeOnboardingMe: build.query<
        GetEmployeeOnboardingMeResponse,
        GetEmployeeOnboardingMeArg
      >({
        query: (queryArg) => ({
          url: `/api/employee_onboarding/v1/auth/${queryArg}/me`,
        }),
        providesTags: ['Employee Onboarding Public'],
      }),
      sendOnboardingOtpCode: build.mutation<
        SendOnboardingOtpCodeResponse,
        SendOnboardingOtpCodeArg
      >({
        query: (queryArg) => ({
          url: `/api/employee_onboarding/v1/auth/${queryArg}/otp-code`,
          method: 'POST',
        }),
        invalidatesTags: ['Employee Onboarding Public'],
      }),
      authenticateOnboarding: build.mutation<
        AuthenticateOnboardingResponse,
        AuthenticateOnboardingArg
      >({
        query: (queryArg) => ({
          url: `/api/employee_onboarding/v1/auth/${queryArg.attemptId}/token`,
          method: 'POST',
          body: queryArg.employeeOnboardingAuthSerializer,
        }),
        invalidatesTags: ['Employee Onboarding Public'],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as appApi };
export type GetProfileResponse =
  /** status 200 Successful Response */ ResponseModelConciergeProfile;
export type GetProfileArg = void;
export type GetAuthorizedUserResponse =
  /** status 200 Successful Response */ ResponseModelUserRetrieveSerializer;
export type GetAuthorizedUserArg = void;
export type UpdateAuthorizedUserResponse =
  /** status 200 Successful Response */ ResponseModelUserRetrieveSerializer;
export type UpdateAuthorizedUserArg = UserUpdateSerializer;
export type UpdateAuthorizedUserPhotoResponse =
  /** status 200 Successful Response */ ResponseModelProfileImageFileKey;
export type UpdateAuthorizedUserPhotoArg = BodyUpdateAuthorizedUserPhoto;
export type GetUserConfigResponse =
  /** status 200 Successful Response */ ResponseModelUserConfigRetrieveSerializer;
export type GetUserConfigArg = void;
export type UpdateUserConfigResponse =
  /** status 200 Successful Response */ ResponseModelUserConfigRetrieveSerializer;
export type UpdateUserConfigArg = UserConfigUpdateSerializer;
export type GetUserEmailNotificationSettingsResponse =
  /** status 200 Successful Response */ ResponseModelUserEmailNotificationSettingsListSerializer;
export type GetUserEmailNotificationSettingsArg = void;
export type UpdateUserEmailNotificationSettingResponse =
  /** status 200 Successful Response */ ResponseModelUserEmailNotificationSettingRetrieveSerializer;
export type UpdateUserEmailNotificationSettingArg = {
  emailType: EmailTemplate;
  userEmailNotificationSettingUpdateSerializer: UserEmailNotificationSettingUpdateSerializer;
};
export type CreateUserRoleResponse =
  /** status 201 Successful Response */ ResponseModelUserRole;
export type CreateUserRoleArg = CreateCompanySerializer;
export type GetUserCompaniesResponse =
  /** status 200 Successful Response */ ResponseModelPageUserRole;
export type GetUserCompaniesArg = {
  all?: boolean;
  idIn?: string[] | null;
  idNotIn?: string[] | null;
  pageNum?: number;
  pageSize?: number;
  activeStatusIn?: UserRoleActiveStatus[] | null;
  search?: string | null;
  allowCrmImport?: boolean;
};
export type UpdateUserRoleResponse =
  /** status 200 Successful Response */ ResponseModelUserRole;
export type UpdateUserRoleArg = {
  userRoleId: string;
  updateUserRoleSerializer: UpdateUserRoleSerializer;
};
export type ListCompanyUsersResponse =
  /** status 200 Successful Response */ ResponseModelPageCompanyUserListSerializer;
export type ListCompanyUsersArg = {
  companyId: string;
  all?: boolean;
  idIn?: string[] | null;
  idNotIn?: string[] | null;
  pageNum?: number;
  pageSize?: number;
  search?: string | null;
};
export type DeactivateCompanyUserResponse = unknown;
export type DeactivateCompanyUserArg = {
  companyId: string;
  userId: string;
};
export type UpdateCompanyResponse =
  /** status 200 Successful Response */ ResponseModelCompany;
export type UpdateCompanyArg = {
  companyId: string;
  updateCompanySerializer: UpdateCompanySerializer;
};
export type GetFullUserRolesResponse =
  /** status 200 Successful Response */ ResponseModelPageFullUserRoleInfo;
export type GetFullUserRolesArg = {
  all?: boolean;
  idIn?: string[] | null;
  idNotIn?: string[] | null;
  pageNum?: number;
  pageSize?: number;
  activeStatusIn?: UserRoleActiveStatus[] | null;
  search?: string | null;
};
export type CountUserRolesResponse =
  /** status 200 Successful Response */ ResponseModelCountUserRolesSerializer;
export type CountUserRolesArg = {
  all?: boolean;
  idIn?: string[] | null;
  idNotIn?: string[] | null;
  pageNum?: number;
  pageSize?: number;
  activeStatusIn?: UserRoleActiveStatus[] | null;
  search?: string | null;
};
export type GetCompanyCheckListResponse =
  /** status 200 Successful Response */ ResponseModelCompanyCheckList;
export type GetCompanyCheckListArg = string;
export type GetCompaniesCheckListResponse =
  /** status 200 Successful Response */ ResponseModelUsersCompaniesCheckLists;
export type GetCompaniesCheckListArg = void;
export type DoneCompanyCheckListItemResponse =
  /** status 200 Successful Response */ ResponseModelCompanyCheckListItem;
export type DoneCompanyCheckListItemArg = {
  companyId: string;
  itemType: CompanyCheckListItemType;
};
export type CreateCompanyInviteResponse =
  /** status 201 Successful Response */ ResponseModelCompanyInvite;
export type CreateCompanyInviteArg = CreateCompanyInviteSerializer;
export type ListEmployeesResponse =
  /** status 200 Successful Response */ ResponseModelPageEmployeeRetrieveSerializer;
export type ListEmployeesArg = {
  all?: boolean;
  idIn?: string[] | null;
  idNotIn?: string[] | null;
  pageNum?: number;
  pageSize?: number;
  ordering?:
    | (
        | 'first_name'
        | '-first_name'
        | 'last_name'
        | '-last_name'
        | 'middle_name'
        | '-middle_name'
      )[]
    | null;
  companyIn?: string[] | null;
  statusIn?: EmployeeStatus[] | null;
  groupIn?: string[] | null;
  leaderIn?: string[] | null;
  signTypeIn?: EmployeeSignType[] | null;
  ipnIn?: (string | string | string)[] | null;
  positionIn?: string[] | null;
  phone?: string | null;
  search?: string | null;
};
export type CreateEmployeeResponse =
  /** status 201 Successful Response */ ResponseModelEmployeeRetrieveSerializer;
export type CreateEmployeeArg = EmployeeCreateSerializer;
export type DeleteEmployeesResponse =
  /** status 200 Successful Response */ ResponseModelEmployeesDeletionResult;
export type DeleteEmployeesArg = {
  all?: boolean;
  idIn?: string[] | null;
  idNotIn?: string[] | null;
  companyIn?: string[] | null;
  statusIn?: EmployeeStatus[] | null;
  groupIn?: string[] | null;
  leaderIn?: string[] | null;
  signTypeIn?: EmployeeSignType[] | null;
  ipnIn?: (string | string | string)[] | null;
  positionIn?: string[] | null;
  phone?: string | null;
  search?: string | null;
};
export type GetEmployeesFiltersResponse =
  /** status 200 Successful Response */ ResponseModelAvailableFilters;
export type GetEmployeesFiltersArg = {
  all?: boolean;
  idIn?: string[] | null;
  idNotIn?: string[] | null;
  pageNum?: number;
  pageSize?: number;
  ordering?:
    | (
        | 'first_name'
        | '-first_name'
        | 'last_name'
        | '-last_name'
        | 'middle_name'
        | '-middle_name'
      )[]
    | null;
  companyIn?: string[] | null;
  statusIn?: EmployeeStatus[] | null;
  groupIn?: string[] | null;
  leaderIn?: string[] | null;
  signTypeIn?: EmployeeSignType[] | null;
  ipnIn?: (string | string | string)[] | null;
  positionIn?: string[] | null;
  phone?: string | null;
  search?: string | null;
};
export type CountEmployeesResponse =
  /** status 200 Successful Response */ ResponseModelEmployeesCountResult;
export type CountEmployeesArg = {
  all?: boolean;
  idIn?: string[] | null;
  idNotIn?: string[] | null;
  companyIn?: string[] | null;
  statusIn?: EmployeeStatus[] | null;
  groupIn?: string[] | null;
  leaderIn?: string[] | null;
  signTypeIn?: EmployeeSignType[] | null;
  ipnIn?: (string | string | string)[] | null;
  positionIn?: string[] | null;
  phone?: string | null;
  search?: string | null;
};
export type ValidateEmployeeResponse =
  /** status 200 Successful Response */ any;
export type ValidateEmployeeArg = EmployeeValidateSerializer;
export type RetrieveEmployeeResponse =
  /** status 200 Successful Response */ ResponseModelEmployeeRetrieveSerializer;
export type RetrieveEmployeeArg = string;
export type UpdateEmployeeResponse =
  /** status 200 Successful Response */ ResponseModelEmployeeRetrieveSerializer;
export type UpdateEmployeeArg = {
  employeeId: string;
  employeeUpdateSerializer: EmployeeUpdateSerializer;
};
export type PartialUpdateEmployeeResponse =
  /** status 200 Successful Response */ ResponseModelEmployeeRetrieveSerializer;
export type PartialUpdateEmployeeArg = {
  employeeId: string;
  partialEmployeeUpdateSerializer: PartialEmployeeUpdateSerializer;
};
export type DeleteEmployeeResponse =
  /** status 200 Successful Response */ ResponseModelEmployeeRetrieveSerializer;
export type DeleteEmployeeArg = string;
export type InviteEmployeesResponse =
  /** status 200 Successful Response */ ResponseModelEmployeesInviteResult;
export type InviteEmployeesArg = {
  all?: boolean;
  idIn?: string[] | null;
  idNotIn?: string[] | null;
  companyIn?: string[] | null;
  statusIn?: EmployeeStatus[] | null;
  groupIn?: string[] | null;
  leaderIn?: string[] | null;
  signTypeIn?: EmployeeSignType[] | null;
  ipnIn?: (string | string | string)[] | null;
  positionIn?: string[] | null;
  phone?: string | null;
  search?: string | null;
};
export type UpdateEmployeePhotoResponse =
  /** status 200 Successful Response */ ResponseModelProfileImageFileKey;
export type UpdateEmployeePhotoArg = {
  employeeId: string;
  bodyUpdateEmployeePhoto: BodyUpdateEmployeePhoto;
};
export type CreateEmployeeAbsencesResponse =
  /** status 200 Successful Response */ ResponseModelAbsence;
export type CreateEmployeeAbsencesArg = {
  employeeId: string;
  createAbsenceSerializer: CreateAbsenceSerializer;
};
export type ListEmployeeAbsencesResponse =
  /** status 200 Successful Response */ ResponseModelPageFullWorkingPeriodAbsence;
export type ListEmployeeAbsencesArg = {
  employeeId: string;
  pageNum?: number;
  pageSize?: number;
};
export type UpdateEmployeeAbsencesResponse =
  /** status 200 Successful Response */ ResponseModelAbsence;
export type UpdateEmployeeAbsencesArg = {
  absenceId: string;
  employeeId: string;
  updateAbsenceSerializer: UpdateAbsenceSerializer;
};
export type UpdateEmployeeWorkingPeriodResponse =
  /** status 200 Successful Response */ ResponseModelWorkingPeriod;
export type UpdateEmployeeWorkingPeriodArg = {
  periodId: string;
  employeeId: string;
  updateWorkingPeriodSerializer: UpdateWorkingPeriodSerializer;
};
export type EmployeeUploadAttachmentResponse =
  /** status 200 Successful Response */ ResponseModelTaskAttachment;
export type EmployeeUploadAttachmentArg = {
  employeeId: string;
  bodyEmployeeUploadAttachment: BodyEmployeeUploadAttachment;
};
export type EmployeeListAttachmentResponse =
  /** status 200 Successful Response */ ResponseModelPageFullTaskAttachment;
export type EmployeeListAttachmentArg = {
  employeeId: string;
  all?: boolean;
  idIn?: string[] | null;
  idNotIn?: string[] | null;
  pageNum?: number;
  pageSize?: number;
  taskIdIn?: string[] | null;
  attachmentTypeIn?: TaskAttachmentType[] | null;
};
export type InviteEmployeeResponse = /** status 200 Successful Response */ any;
export type InviteEmployeeArg = string;
export type RestoreEmployeeResponse =
  /** status 200 Successful Response */ ResponseModelEmployeeRetrieveSerializer;
export type RestoreEmployeeArg = string;
export type ChangeEmployeeSigningTypeResponse =
  /** status 200 Successful Response */ ResponseModelEmployeeRetrieveSerializer;
export type ChangeEmployeeSigningTypeArg = {
  employeeId: string;
  updateSigningTypeSerializer: UpdateSigningTypeSerializer;
};
export type ListEmployeeDocumentsResponse =
  /** status 200 Successful Response */ ResponseModelPageEmployeeDocumentRetrieveSerializer;
export type ListEmployeeDocumentsArg = {
  employeeId: string;
  all?: boolean;
  idIn?: string[] | null;
  idNotIn?: string[] | null;
  pageNum?: number;
  pageSize?: number;
  ordering?: ('date_created' | '-date_created')[] | null;
  documentTypeIn?: EmployeeDocumentType[] | null;
};
export type CreateGroupResponse =
  /** status 201 Successful Response */ ResponseModelGroupRetrieveSerializer;
export type CreateGroupArg = GroupCreateSerializer;
export type ListGroupsResponse =
  /** status 200 Successful Response */ ResponseModelPageGroupRetrieveSerializer;
export type ListGroupsArg = {
  pageNum?: number;
  pageSize?: number;
  companyId: string;
  search?: string | null;
};
export type UpdateGroupResponse =
  /** status 200 Successful Response */ ResponseModelGroupRetrieveSerializer;
export type UpdateGroupArg = {
  groupId: string;
  groupUpdateSerializer: GroupUpdateSerializer;
};
export type CreateFileImportUploadResponse =
  /** status 202 Successful Response */ ResponseModelTaskResponseModel;
export type CreateFileImportUploadArg = {
  importType: ImportTaskType;
  companyId: string;
  bodyCreateFileImportUpload: BodyCreateFileImportUpload;
};
export type GetFileImportUploadResponse =
  /** status 200 Successful Response */ ResponseModelFileImportTask;
export type GetFileImportUploadArg = string;
export type CreateVacationTaskTemplateResponse =
  /** status 201 Successful Response */ ResponseModelVacationTaskTemplateRetrieveSerializer;
export type CreateVacationTaskTemplateArg =
  VacationTaskTemplateCreateSerializer;
export type GenerateTaskTemplateDocumentPreviewResponse =
  /** status 200 Successful Response */ any;
export type GenerateTaskTemplateDocumentPreviewArg =
  VacationTaskTemplateDocumentPreviewSerializer;
export type RetrieveVacationTaskTemplateResponse =
  /** status 200 Successful Response */ ResponseModelVacationTaskTemplateRetrieveSerializer;
export type RetrieveVacationTaskTemplateArg = string;
export type UpdateVacationTaskTemplateResponse =
  /** status 200 Successful Response */ ResponseModelVacationTaskTemplateRetrieveSerializer;
export type UpdateVacationTaskTemplateArg = {
  taskVersionIdentifier: string;
  vacationTaskTemplateUpdateSerializer: VacationTaskTemplateUpdateSerializer;
};
export type UpdateVacationTaskTemplateManagerResponse =
  /** status 200 Successful Response */ ResponseModelVacationTaskTemplateRetrieveSerializer;
export type UpdateVacationTaskTemplateManagerArg = {
  taskVersionIdentifier: string;
  vacationTaskTemplateManagerSerializer: VacationTaskTemplateManagerSerializer;
};
export type GetTemplatePeriodsResponse =
  /** status 200 Successful Response */ ResponseModelAvailablePeriodDatesSerializer;
export type GetTemplatePeriodsArg = {
  taskVersionIdentifier: string;
  employeeId: string;
};
export type ListTaskTemplatesResponse =
  /** status 200 Successful Response */ ResponseModelPageTaskTemplateListSerializer;
export type ListTaskTemplatesArg = {
  all?: boolean;
  idIn?: string[] | null;
  idNotIn?: string[] | null;
  pageNum?: number;
  pageSize?: number;
  companyIn?: string[] | null;
  statusIn?: TaskTemplateStatus[] | null;
  taskTypeIn?: TaskTemplateType[] | null;
  taskTypeNotIn?: TaskTemplateType[] | null;
  search?: string | null;
  isOnboarding?: boolean;
};
export type ListTaskTemplatesByBatchResponse =
  /** status 200 Successful Response */ ResponseModelPageTaskTemplateListSerializer;
export type ListTaskTemplatesByBatchArg = {
  taskTemplateBatch: TaskTemplateBatch;
  all?: boolean;
  idIn?: string[] | null;
  idNotIn?: string[] | null;
  pageNum?: number;
  pageSize?: number;
  companyIn?: string[] | null;
  statusIn?: TaskTemplateStatus[] | null;
  taskTypeIn?: TaskTemplateType[] | null;
  taskTypeNotIn?: TaskTemplateType[] | null;
  search?: string | null;
  isOnboarding?: boolean;
};
export type ToggleActiveTaskTemplatesResponse =
  /** status 200 Successful Response */ ResponseModelVacationTaskTemplateRetrieveSerializer;
export type ToggleActiveTaskTemplatesArg = string;
export type CountTasksResponse =
  /** status 200 Successful Response */ ResponseModelTaskCountResult;
export type CountTasksArg = {
  all?: boolean;
  idIn?: string[] | null;
  idNotIn?: string[] | null;
  pageNum?: number;
  pageSize?: number;
  companyIn?: string[] | null;
  taskTemplateIn?: string[] | null;
  statusIn?: TaskStatus[] | null;
  startDate?: string | null;
  endDate?: string | null;
  managerIn?: string[] | null;
  search?: string | null;
  requireAttention?: boolean | null;
};
export type GetTaskFiltersResponse =
  /** status 200 Successful Response */ ResponseModelAvailableFilters;
export type GetTaskFiltersArg = {
  all?: boolean;
  idIn?: string[] | null;
  idNotIn?: string[] | null;
  pageNum?: number;
  pageSize?: number;
  companyIn?: string[] | null;
  taskTemplateIn?: string[] | null;
  statusIn?: TaskStatus[] | null;
  startDate?: string | null;
  endDate?: string | null;
  managerIn?: string[] | null;
  search?: string | null;
  requireAttention?: boolean | null;
};
export type GetTaskDocumentPreviewResponse =
  /** status 200 Successful Response */ any;
export type GetTaskDocumentPreviewArg = {
  taskId: string;
  documentId: string;
};
export type DeleteTaskDocumentResponse = unknown;
export type DeleteTaskDocumentArg = {
  taskId: string;
  documentId: string;
};
export type TaskUploadDocumentResponse =
  /** status 201 Successful Response */ ResponseModelTaskDocumentRetrieveSerializer;
export type TaskUploadDocumentArg = {
  taskId: string;
  bodyTaskUploadDocument: BodyTaskUploadDocument;
};
export type ListTaskDocumentsResponse =
  /** status 200 Successful Response */ ResponseModelPageTaskDocumentRetrieveSerializer;
export type ListTaskDocumentsArg = string;
export type GetTaskOperationsResponse =
  /** status 200 Successful Response */ ResponseModelTaskOperations;
export type GetTaskOperationsArg = string;
export type GetTaskErrorsResponse =
  /** status 200 Successful Response */ ResponseModelTaskErrorsSerializer;
export type GetTaskErrorsArg = string;
export type CreateTaskResponse =
  /** status 201 Successful Response */ ResponseModelTaskRetrieveSerializer;
export type CreateTaskArg = TaskCreateSerializer;
export type ListTasksResponse =
  /** status 200 Successful Response */ ResponseModelPageTaskListSerializer;
export type ListTasksArg = {
  all?: boolean;
  idIn?: string[] | null;
  idNotIn?: string[] | null;
  pageNum?: number;
  pageSize?: number;
  companyIn?: string[] | null;
  taskTemplateIn?: string[] | null;
  statusIn?: TaskStatus[] | null;
  startDate?: string | null;
  endDate?: string | null;
  managerIn?: string[] | null;
  search?: string | null;
  requireAttention?: boolean | null;
};
export type DeleteTaskResponse = unknown;
export type DeleteTaskArg = string;
export type RetrieveTaskResponse =
  /** status 200 Successful Response */ ResponseModelTaskRetrieveSerializer;
export type RetrieveTaskArg = string;
export type UpdateTaskResponse =
  /** status 200 Successful Response */ ResponseModelTaskRetrieveSerializer;
export type UpdateTaskArg = {
  taskId: string;
  taskUpdateSerializer: TaskUpdateSerializer;
};
export type CancelTaskResponse =
  /** status 200 Successful Response */ ResponseModelTaskRetrieveSerializer;
export type CancelTaskArg = {
  taskId: string;
  taskCancelSerializer: TaskCancelSerializer;
};
export type SendDocumentsResponse =
  /** status 200 Successful Response */ ResponseModelTaskRetrieveSerializer;
export type SendDocumentsArg = {
  taskId: string;
  sendTaskDocumentsSerializer: SendTaskDocumentsSerializer;
};
export type GetTaskPeriodsResponse =
  /** status 200 Successful Response */ ResponseModelAvailablePeriodDatesSerializer;
export type GetTaskPeriodsArg = string;
export type TaskUploadAttachmentResponse =
  /** status 200 Successful Response */ ResponseModelTaskAttachment;
export type TaskUploadAttachmentArg = {
  taskId: string;
  bodyTaskUploadAttachment: BodyTaskUploadAttachment;
};
export type TaskFullRouteEditResponse =
  /** status 200 Successful Response */ ResponseModelTaskRetrieveSerializer;
export type TaskFullRouteEditArg = {
  taskId: string;
  taskFullRouteUpdateSerializer: TaskFullRouteUpdateSerializer;
};
export type TaskRouteDeleteResponse = unknown;
export type TaskRouteDeleteArg = {
  taskId: string;
  routeId: string;
};
export type TaskRouteEditResponse =
  /** status 200 Successful Response */ ResponseModelTaskRetrieveSerializer;
export type TaskRouteEditArg = {
  taskId: string;
  routeId: string;
  taskRouteUpdateSerializer: TaskRouteUpdateSerializer;
};
export type TaskRouteDocumentPreviewResponse =
  /** status 200 Successful Response */ any;
export type TaskRouteDocumentPreviewArg = {
  taskId: string;
  routeId: string;
};
export type TaskRouteDocumentPreviewWithParametersResponse =
  /** status 200 Successful Response */ any;
export type TaskRouteDocumentPreviewWithParametersArg = {
  taskId: string;
  routeId: string;
  sendTaskDocumentSerializer: SendTaskDocumentSerializer;
};
export type TaskRouteActionRemindToActionResponse =
  /** status 200 Successful Response */ ResponseModelRemindToActionResponseSerializer;
export type TaskRouteActionRemindToActionArg = {
  taskId: string;
  routeId: string;
  actionId: string;
};
export type GetUploadedFileResponse = /** status 200 Successful Response */ any;
export type GetUploadedFileArg = string;
export type GetUploadedFileByIdResponse =
  /** status 200 Successful Response */ any;
export type GetUploadedFileByIdArg = string;
export type CreatePublicTokenResponse =
  /** status 201 Successful Response */ ResponseModelCreatedPublicApiTokenSerializer;
export type CreatePublicTokenArg = CreatePublicApiToken;
export type ListCompanyPublicTokensResponse =
  /** status 200 Successful Response */ ResponseModelPublicTokenListSerializer;
export type ListCompanyPublicTokensArg = string;
export type UpdatePublicTokenResponse =
  /** status 200 Successful Response */ ResponseModelPublicTokenRetrieveSerializer;
export type UpdatePublicTokenArg = {
  tokenId: string;
  updatePublicApiTokenSerializer: UpdatePublicApiTokenSerializer;
};
export type DeletePublicTokenResponse = unknown;
export type DeletePublicTokenArg = string;
export type CreateOnboardingAttemptResponse =
  /** status 201 Successful Response */ ResponseModelEmployeeOnboardingAttemptReceiveSerializer;
export type CreateOnboardingAttemptArg =
  CreateEmployeeOnboardingAttemptSerializer;
export type UpdateOnboardingAttemptResponse =
  /** status 200 Successful Response */ ResponseModelEmployeeOnboardingAttemptReceiveSerializer;
export type UpdateOnboardingAttemptArg = {
  attemptId: string;
  updateEmployeeOnboardingAttemptSerializer: UpdateEmployeeOnboardingAttemptSerializer;
};
export type GetDiiaOnboardingDeeplinkResponse =
  /** status 200 Successful Response */ ResponseModelEmployeeOnboardingDeeplinkReceiveSerializer;
export type GetDiiaOnboardingDeeplinkArg = string;
export type VchasnoMobileEmployeesResponse =
  /** status 200 Successful Response */ ResponseModelVchasnoEmployeesSerializer;
export type VchasnoMobileEmployeesArg = {
  vchasnoId: string;
  all?: boolean;
  idIn?: string[] | null;
  idNotIn?: string[] | null;
  ordering?:
    | (
        | 'first_name'
        | '-first_name'
        | 'last_name'
        | '-last_name'
        | 'middle_name'
        | '-middle_name'
      )[]
    | null;
  statusIn?: EmployeeStatus[] | null;
};
export type VchasnoMobileEmployeeByIdResponse =
  /** status 200 Successful Response */ ResponseModelFullEmployee;
export type VchasnoMobileEmployeeByIdArg = {
  employeeId: string;
  vchasnoId: string;
};
export type VchasnoMobileEmployeeTaskTemplateResponse =
  /** status 200 Successful Response */ ResponseModelPageEmployeeTaskTemplate;
export type VchasnoMobileEmployeeTaskTemplateArg = {
  employeeId: string;
  vchasnoId: string;
  all?: boolean;
  idIn?: string[] | null;
  idNotIn?: string[] | null;
  pageNum?: number;
  pageSize?: number;
  statusIn?: TaskTemplateStatus[] | null;
  search?: string | null;
  taskTypeIn?: TaskTemplateType[] | null;
};
export type VchasnoMobileEmployeeTaskCreateResponse =
  /** status 201 Successful Response */ ResponseModelTask;
export type VchasnoMobileEmployeeTaskCreateArg = {
  employeeId: string;
  vchasnoId: string;
  employeeTaskCreateSerializer: EmployeeTaskCreateSerializer;
};
export type VchasnoMobileEmployeeTaskUpdateResponse =
  /** status 200 Successful Response */ ResponseModelTask;
export type VchasnoMobileEmployeeTaskUpdateArg = {
  taskId: string;
  employeeId: string;
  vchasnoId: string;
  employeeTaskUpdateSerializer: EmployeeTaskUpdateSerializer;
};
export type VchasnoMobileEmployeeTaskResponse =
  /** status 200 Successful Response */ ResponseModelPageEmployeeTaskFull;
export type VchasnoMobileEmployeeTaskArg = {
  employeeId: string;
  vchasnoId: string;
  all?: boolean;
  idIn?: string[] | null;
  idNotIn?: string[] | null;
  pageNum?: number;
  pageSize?: number;
  statusIn?: TaskStatus[] | null;
  search?: string | null;
  routeStatusIn?: TaskRouteStatus[] | null;
};
export type VchasnoMobileEmployeeTaskToActionResponse =
  /** status 200 Successful Response */ ResponseModelPageEmployeeTaskFull;
export type VchasnoMobileEmployeeTaskToActionArg = {
  employeeId: string;
  vchasnoId: string;
  all?: boolean;
  idIn?: string[] | null;
  idNotIn?: string[] | null;
  pageNum?: number;
  pageSize?: number;
  statusIn?: TaskStatus[] | null;
  search?: string | null;
  routeStatusIn?: TaskRouteStatus[] | null;
};
export type VchasnoMobileEmployeeTaskByIdResponse =
  /** status 200 Successful Response */ ResponseModelEmployeeTaskFull;
export type VchasnoMobileEmployeeTaskByIdArg = {
  taskId: string;
  employeeId: string;
  vchasnoId: string;
};
export type VchasnoMobileEmployeeTaskToActionCountResponse =
  /** status 200 Successful Response */ ResponseModelCountEmployeeTaskToActionSerializer;
export type VchasnoMobileEmployeeTaskToActionCountArg = {
  employeeId: string;
  vchasnoId: string;
  all?: boolean;
  idIn?: string[] | null;
  idNotIn?: string[] | null;
  pageNum?: number;
  pageSize?: number;
  statusIn?: TaskStatus[] | null;
  search?: string | null;
  routeStatusIn?: TaskRouteStatus[] | null;
};
export type VchasnoMobileCancelTaskResponse =
  /** status 200 Successful Response */ ResponseModelEmployeeTaskFull;
export type VchasnoMobileCancelTaskArg = {
  taskId: string;
  employeeId: string;
  vchasnoId: string;
  taskCancelSerializer: TaskCancelSerializer;
};
export type VchasnoMobileApproveTaskResponse =
  /** status 200 Successful Response */ ResponseModelEmployeeTaskFull;
export type VchasnoMobileApproveTaskArg = {
  actionStatus: TaskRouteActionStatus;
  taskId: string;
  employeeId: string;
  vchasnoId: string;
  body: LeaderTaskRouteActionStatusChangeSerializer | null;
};
export type MobileGetUploadedFileResponse =
  /** status 200 Successful Response */ any;
export type MobileGetUploadedFileArg = {
  fileKey: string;
  employeeId: string;
  vchasnoId: string;
};
export type UpdateCompanyMaxEmployeesResponse =
  /** status 200 Successful Response */ ResponseModelInternalCompany;
export type UpdateCompanyMaxEmployeesArg = {
  companyId: string;
  companyMaxEmployeesUpdateSerializer: CompanyMaxEmployeesUpdateSerializer;
};
export type ListCompanyAdminResponse =
  /** status 200 Successful Response */ ResponseModelPageInternalCompany;
export type ListCompanyAdminArg = {
  all?: boolean;
  idIn?: string[] | null;
  idNotIn?: string[] | null;
  pageNum?: number;
  pageSize?: number;
  search?: string | null;
  planIn?: CompanyPlanType[] | null;
};
export type ChangeCompanyPlanResponse =
  /** status 200 Successful Response */ ResponseModelInternalCompany;
export type ChangeCompanyPlanArg = {
  companyId: string;
  changePlanSerializer: ChangePlanSerializer;
};
export type ResyncCompanyTemplatesResponse =
  /** status 200 Successful Response */ ResponseModelInternalCompany;
export type ResyncCompanyTemplatesArg = string;
export type UpdateUserInternalRoleResponse =
  /** status 200 Successful Response */ ResponseModelInternalUser;
export type UpdateUserInternalRoleArg = {
  userId: string;
  updateInternalUserRoleSerializer: UpdateInternalUserRoleSerializer;
};
export type AdminAddEdoRoleResponse = /** status 200 Successful Response */ any;
export type AdminAddEdoRoleArg = AddEdoRoleSerializer;
export type AdminRemoveEdoRoleResponse =
  /** status 200 Successful Response */ any;
export type AdminRemoveEdoRoleArg = RemoveEdoRoleSerializer;
export type AdminInviteEmployeeResponse =
  /** status 200 Successful Response */ any;
export type AdminInviteEmployeeArg = string;
export type CreatePublicApiTokenResponse =
  /** status 201 Successful Response */ ResponseModelCreatedPublicApiTokenSerializer;
export type CreatePublicApiTokenArg = CreatePublicApiToken;
export type UpdatePublicApiTokenResponse =
  /** status 200 Successful Response */ ResponseModelPublicTokenRetrieveSerializer;
export type UpdatePublicApiTokenArg = {
  tokenId: string;
  updatePublicApiTokenSerializer: UpdatePublicApiTokenSerializer;
};
export type AdminUploadTaskTemplateDocumentResponse =
  /** status 200 Successful Response */ any;
export type AdminUploadTaskTemplateDocumentArg = {
  documentId: string;
  bodyAdminUploadTaskTemplateDocument: BodyAdminUploadTaskTemplateDocument;
};
export type AdminSetTaskTemplateDocumentGenerationParamsResponse =
  /** status 200 Successful Response */ any;
export type AdminSetTaskTemplateDocumentGenerationParamsArg = {
  documentId: string;
  taskDocumentGenerationParamsSerializerInput: TaskDocumentGenerationParamsSerializer2;
};
export type PublicCreateEmployeeResponse =
  /** status 201 Successful Response */ ResponseModelPublicApiEmployeeRetrieveSerializer;
export type PublicCreateEmployeeArg = PublicApiCreateEmployeeSerializer;
export type PublicListEmployeesResponse =
  /** status 200 Successful Response */ ResponseModelPagePublicApiEmployeeRetrieveSerializer;
export type PublicListEmployeesArg = {
  all?: boolean;
  idIn?: string[] | null;
  idNotIn?: string[] | null;
  pageNum?: number;
  pageSize?: number;
  ordering?: string[] | null;
  statusIn?: EmployeeStatus[] | null;
  groupIn?: string[] | null;
  leaderIn?: string[] | null;
  signTypeIn?: EmployeeSignType[] | null;
  positionIn?: string[] | null;
};
export type PublicUpdateEmployeeResponse =
  /** status 200 Successful Response */ ResponseModelPublicApiEmployeeRetrieveSerializer;
export type PublicUpdateEmployeeArg = {
  employeeId: string;
  publicApiUpdateEmployeeSerializer: PublicApiUpdateEmployeeSerializer;
};
export type PublicDeleteEmployeeResponse =
  /** status 200 Successful Response */ ResponseModelPublicApiEmployeeRetrieveSerializer;
export type PublicDeleteEmployeeArg = string;
export type PublicGetEmployeeResponse =
  /** status 200 Successful Response */ ResponseModelPublicApiEmployeeRetrieveSerializer;
export type PublicGetEmployeeArg = string;
export type PublicCreateEmployeeAbsencesResponse =
  /** status 200 Successful Response */ ResponseModelAbsence;
export type PublicCreateEmployeeAbsencesArg = {
  employeeId: string;
  createAbsenceSerializer: CreateAbsenceSerializer;
};
export type PublicListEmployeeAbsencesResponse =
  /** status 200 Successful Response */ ResponseModelPageFullWorkingPeriodAbsence;
export type PublicListEmployeeAbsencesArg = {
  employeeId: string;
  pageNum?: number;
  pageSize?: number;
};
export type PublicUpdateEmployeeAbsencesResponse =
  /** status 200 Successful Response */ ResponseModelAbsence;
export type PublicUpdateEmployeeAbsencesArg = {
  absenceId: string;
  employeeId: string;
  updateAbsenceSerializer: UpdateAbsenceSerializer;
};
export type PublicListGroupsResponse =
  /** status 200 Successful Response */ ResponseModelPagePublicApiGroupRetrieveSerializer;
export type PublicListGroupsArg = {
  ordering?: string[] | null;
  all?: boolean;
  idIn?: string[] | null;
  idNotIn?: string[] | null;
  pageNum?: number;
  pageSize?: number;
  search?: string | null;
};
export type PublicCreateGroupResponse =
  /** status 201 Successful Response */ ResponseModelPublicApiGroupRetrieveSerializer;
export type PublicCreateGroupArg = PublicApiCreateGroupSerializer;
export type PublicGetGroupResponse =
  /** status 200 Successful Response */ ResponseModelPublicApiGroupRetrieveSerializer;
export type PublicGetGroupArg = string;
export type PublicUpdateGroupResponse =
  /** status 200 Successful Response */ ResponseModelPublicApiGroupRetrieveSerializer;
export type PublicUpdateGroupArg = {
  groupId: string;
  publicApiUpdateGroupSerializer: PublicApiUpdateGroupSerializer;
};
export type PublicDeleteGroupResponse =
  /** status 200 Successful Response */ ResponseModelPublicApiGroupDeleteSerializer;
export type PublicDeleteGroupArg = string;
export type PublicListVacationDocumentsResponse =
  /** status 200 Successful Response */ ResponseModelPagePublicApiListVacationDocumentSerializer;
export type PublicListVacationDocumentsArg = string;
export type PublicListVacationDocumentSignersResponse =
  /** status 200 Successful Response */ ResponseModelPagePublicApiRetrieveDocumentSignerSerializer;
export type PublicListVacationDocumentSignersArg = {
  taskId: string;
  documentId: string;
};
export type PublicCreateVacationDocumentSignerResponse =
  /** status 201 Successful Response */ ResponseModelPublicApiRetrieveDocumentSignerSerializer;
export type PublicCreateVacationDocumentSignerArg = {
  taskId: string;
  documentId: string;
  publicApiCreateDocumentSignerSerializer: PublicApiCreateDocumentSignerSerializer;
};
export type PublicDeleteVacationDocumentSignerResponse = unknown;
export type PublicDeleteVacationDocumentSignerArg = {
  taskId: string;
  documentId: string;
  executorId: string;
};
export type PublicSendVacationDocumentsResponse =
  /** status 200 Successful Response */ ResponseModelPublicApiRetrieveVacationSerializer;
export type PublicSendVacationDocumentsArg = {
  taskId: string;
  sendVacationDocumentsSerializer: SendVacationDocumentsSerializer;
};
export type PublicListUniversalDocumentsResponse =
  /** status 200 Successful Response */ ResponseModelPagePublicApiRetrieveUniversalDocumentSerializer;
export type PublicListUniversalDocumentsArg = {
  all?: boolean;
  idIn?: string[] | null;
  idNotIn?: string[] | null;
  pageNum?: number;
  pageSize?: number;
  companyIn?: string[] | null;
  taskTemplateIn?: string[] | null;
  statusIn?: TaskStatus[] | null;
  startDate?: string | null;
  endDate?: string | null;
  managerIn?: string[] | null;
  search?: string | null;
  requireAttention?: boolean | null;
};
export type PublicListUniversalDocumentDocumentsResponse =
  /** status 200 Successful Response */ ResponseModelPagePublicApiListUniversalDocumentDocumentSerializer;
export type PublicListUniversalDocumentDocumentsArg = string;
export type PublicListUniversalDocumentDocumentSignersResponse =
  /** status 200 Successful Response */ ResponseModelPagePublicApiRetrieveDocumentSignerSerializer;
export type PublicListUniversalDocumentDocumentSignersArg = {
  taskId: string;
  documentId: string;
};
export type PublicCreateUniversalDocumentDocumentSignerResponse =
  /** status 201 Successful Response */ ResponseModelPublicApiRetrieveDocumentSignerSerializer;
export type PublicCreateUniversalDocumentDocumentSignerArg = {
  taskId: string;
  documentId: string;
  publicApiCreateDocumentSignerSerializer: PublicApiCreateDocumentSignerSerializer;
};
export type PublicDeleteUniversalDocumentDocumentSignerResponse = unknown;
export type PublicDeleteUniversalDocumentDocumentSignerArg = {
  taskId: string;
  documentId: string;
  executorId: string;
};
export type PublicSendUniversalDocumentDocumentsResponse =
  /** status 200 Successful Response */ ResponseModelPublicApiRetrieveUniversalDocumentSerializer;
export type PublicSendUniversalDocumentDocumentsArg = {
  taskId: string;
  sendUniversalDocumentsSerializer: SendUniversalDocumentsSerializer;
};
export type HandleFilesBatchUploadResponse =
  /** status 200 Successful Response */ SuccessResponse;
export type HandleFilesBatchUploadArg = {
  'X-Document-Request-Trace-Id': string;
  bodyHandleFilesBatchUpload: BodyHandleFilesBatchUpload;
};
export type GetEmployeeOnboardingMeResponse =
  /** status 200 Successful Response */ ResponseModelFullEmployee;
export type GetEmployeeOnboardingMeArg = string;
export type SendOnboardingOtpCodeResponse =
  /** status 200 Successful Response */ ResponseModelEmployeeOnboardingOtpSendSerializer;
export type SendOnboardingOtpCodeArg = string;
export type AuthenticateOnboardingResponse =
  /** status 200 Successful Response */ ResponseModelEmployeeOnboardingTokenSerializer;
export type AuthenticateOnboardingArg = {
  attemptId: string;
  employeeOnboardingAuthSerializer: EmployeeOnboardingAuthSerializer;
};
export type ConciergeProfile = {
  user_id: string;
  email: string | null;
  is_email_confirmed: boolean;
  phone: string | null;
  first_name: string | null;
  last_name: string | null;
  auth_phone: string | null;
};
export type ResponseModelConciergeProfile = {
  detail: ConciergeProfile;
};
export type ValidationErrorModel = {
  loc: (string | number)[];
  msg: string;
  type: string;
  input?: string | null;
  ctx?: {
    [key: string]: any;
  } | null;
};
export type ValidationErrorResponseModel = {
  error_code?: 'ValidationError';
  detail: ValidationErrorModel[];
  error_message?: string | null;
};
export type Company = {
  edrpou: string | string | string;
  name: string | null;
  color: string | null;
  leader_id: string | null;
  max_employees_count: number;
  id: string;
};
export type ProfileImageFileKey = {
  /** Ключ файлу на S3 */
  orig_img: string;
  square_orig?: string | null;
  square_mid?: string | null;
  square_small?: string | null;
};
export type UserRetrieveSerializer = {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  middle_name?: string | null;
  /** Full name */
  full_name?: string | null;
  phone?: string | null;
  email: string;
  is_email_confirmed: boolean;
  companies: Company[];
  profile_image?: ProfileImageFileKey | null;
  locale: Language;
};
export type ResponseModelUserRetrieveSerializer = {
  detail: UserRetrieveSerializer;
};
export type DescriptiveErrorModel = {
  type: string;
  msg: string;
};
export type MfaErrorResponseModel = {
  error_code: string;
  detail: DescriptiveErrorModel;
  error_message?: string | null;
};
export type UserUpdateSerializer = {
  /** Ім'я. Повинно починатися з великої літери. */
  first_name: string;
  /** Прізвище. Повинно починатися з великої літери. */
  last_name: string;
  middle_name?: string | null;
  phone?: string | null;
  locale?: Language | null;
  /** 2FA code to change phone number */
  code?: string | null;
};
export type ResponseModelProfileImageFileKey = {
  detail: ProfileImageFileKey;
};
export type ImageInvalidErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type ImageFileTooLargeErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type ImageCropErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type BodyUpdateAuthorizedUserPhoto = {
  image: Blob;
};
export type UserConfigRetrieveSerializer = {
  frontend_config?: {
    [key: string]: any;
  } | null;
};
export type ResponseModelUserConfigRetrieveSerializer = {
  detail: UserConfigRetrieveSerializer;
};
export type UserConfigUpdateSerializer = {
  frontend_config: {
    [key: string]: any;
  };
};
export type UserEmailNotificationSettingRetrieveSerializer = {
  email_type: EmailTemplate;
  email_name: string;
  status: NotificationActiveStatus;
};
export type UserEmailNotificationSettingsListSerializer = {
  settings: UserEmailNotificationSettingRetrieveSerializer[];
};
export type ResponseModelUserEmailNotificationSettingsListSerializer = {
  detail: UserEmailNotificationSettingsListSerializer;
};
export type ResponseModelUserEmailNotificationSettingRetrieveSerializer = {
  detail: UserEmailNotificationSettingRetrieveSerializer;
};
export type UserEmailNotificationSettingUpdateSerializer = {
  status: NotificationActiveStatus;
};
export type User = {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  status: UserStatus;
  middle_name?: string | null;
  vchasno_id?: string | null;
  photo_media_file_id?: string | null;
  locale?: Language;
  profile_image?: ProfileImageFileKey | null;
};
export type UserRole = {
  can_invite_coworkers: boolean;
  can_generate_api_keys: boolean;
  can_change_company_info: boolean;
  can_view_company_logs: boolean;
  id: string;
  user: User;
  company: Company;
  role: UserCompanyRole | null;
  active_status: UserRoleActiveStatus;
  can_change_activity_status: boolean;
};
export type ResponseModelUserRole = {
  detail: UserRole;
};
export type UserRoleNotUniqueResponseModel = {
  error_code: string;
  detail: UserRole;
  error_message?: string | null;
};
export type CompanySingValidationErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type CompanyNotUniqueErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type UnauthorizedErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type CreateCompanySerializer = {
  /** Sign the user_id with the company's key to gain access. */
  signature: Blob;
};
export type PageUserRole = {
  page_num: number;
  page_size: number;
  has_prev: boolean;
  has_next: boolean;
  total_items: number | null;
  total_pages: number | null;
  items: UserRole[];
};
export type ResponseModelPageUserRole = {
  detail: PageUserRole;
};
export type NotFoundErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type UpdateUserRoleSerializer = {
  active_status: UserRoleActiveStatus;
};
export type CompanyUserListSerializer = {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  middle_name?: string | null;
  phone?: string | null;
  email: string;
  status: UserRoleActiveStatus;
  profile_image?: ProfileImageFileKey | null;
};
export type PageCompanyUserListSerializer = {
  page_num: number;
  page_size: number;
  has_prev: boolean;
  has_next: boolean;
  total_items: number | null;
  total_pages: number | null;
  items: CompanyUserListSerializer[];
};
export type ResponseModelPageCompanyUserListSerializer = {
  detail: PageCompanyUserListSerializer;
};
export type CompanyAccessErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type UserDeactivationErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type ResponseModelCompany = {
  detail: Company;
};
export type InvalidLeaderErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type UpdateCompanySerializer = {
  leader_id?: string | null;
  color?: string | null;
  name?: string | null;
};
export type Employee = {
  id: string;
  last_name: string;
  first_name?: string | null;
  middle_name?: string | null;
  ipn?: string | string | string | null;
  email?: string | null;
  work_email?: string | null;
  phone?: string | null;
  position?: string | null;
  date_joined?: string | null;
  date_of_birth?: string | null;
  company_number?: string | null;
  group_id?: string | null;
  leader_id?: string | null;
  managerial_leader_id?: string | null;
  company_id: string;
  status: EmployeeStatus;
  photo_media_file_id?: string | null;
  sign_type?: EmployeeSignType;
  invite_sent_at?: string | null;
};
export type FullCompanyInfo = {
  edrpou: string | string | string;
  name: string | null;
  color: string | null;
  leader_id: string | null;
  max_employees_count: number;
  id: string;
  leader?: Employee | null;
  employees_number?: number | null;
};
export type FullUserRoleInfo = {
  can_invite_coworkers: boolean;
  can_generate_api_keys: boolean;
  can_change_company_info: boolean;
  can_view_company_logs: boolean;
  id: string;
  user: User;
  company: FullCompanyInfo;
  role: UserCompanyRole | null;
  active_status: UserRoleActiveStatus;
  can_change_activity_status: boolean;
};
export type PageFullUserRoleInfo = {
  page_num: number;
  page_size: number;
  has_prev: boolean;
  has_next: boolean;
  total_items: number | null;
  total_pages: number | null;
  items: FullUserRoleInfo[];
};
export type ResponseModelPageFullUserRoleInfo = {
  detail: PageFullUserRoleInfo;
};
export type UserRoleFilterParams = {
  all?: boolean;
  id__in?: string[] | null;
  id__not_in?: string[] | null;
  page_num?: number;
  page_size?: number;
  active_status__in?: UserRoleActiveStatus[] | null;
  search?: string | null;
};
export type CountUserRolesSerializer = {
  count: number;
  params: UserRoleFilterParams;
};
export type ResponseModelCountUserRolesSerializer = {
  detail: CountUserRolesSerializer;
};
export type CompanyCheckListItem = {
  company_id: string;
  progress_status?: CompanyCheckListItemProgressStatus;
  item_type: CompanyCheckListItemType;
  id: string;
};
export type CompanyCheckList = {
  company: Company;
  items: CompanyCheckListItem[];
};
export type ResponseModelCompanyCheckList = {
  detail: CompanyCheckList;
};
export type CompanyCheckListSerializer = {
  user_role: UserRole;
  items: CompanyCheckListItem[];
};
export type UsersCompaniesCheckLists = {
  check_lists: CompanyCheckListSerializer[];
};
export type ResponseModelUsersCompaniesCheckLists = {
  detail: UsersCompaniesCheckLists;
};
export type ResponseModelCompanyCheckListItem = {
  detail: CompanyCheckListItem;
};
export type CompanyInvite = {
  invite_email: string;
  invitor_id: string;
  user_id?: string | null;
  status: CompanyInviteStatus;
  companies_ids?: string[];
  id: string;
};
export type ResponseModelCompanyInvite = {
  detail: CompanyInvite;
};
export type MultipleCompaniesInviteNotAvailableErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type CreateCompanyInviteSerializer = {
  invite_email: string;
  companies_ids: string[];
};
export type CompanyRetrieveSerializer = {
  id: string;
  name: string | null;
};
export type LeaderRetrieveSerializer = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  middle_name?: string | null;
  status: EmployeeStatus;
  profile_photo?: ProfileImageFileKey | null;
};
export type GroupRetrieveSerializer = {
  id: string;
  name: string;
};
export type EmployeeOnboardingAttempt = {
  company_id: string;
  diia_offer_id?: string | null;
  employee_id?: string | null;
  user_id: string;
  phone: string;
  status?: EmployeeOnboardingAttemptStatus;
  id: string;
};
export type EmployeeRetrieveSerializer = {
  ipn?: string | string | string | null;
  email?: string | null;
  work_email?: string | null;
  phone?: string | null;
  position?: string | null;
  date_joined?: string | null;
  date_of_birth?: string | null;
  company_number?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  middle_name?: string | null;
  company: CompanyRetrieveSerializer;
  id: string;
  status: EmployeeStatus;
  leader?: LeaderRetrieveSerializer | null;
  managerial_leader?: LeaderRetrieveSerializer | null;
  group?: GroupRetrieveSerializer | null;
  vacation_days: number | null;
  profile_photo: ProfileImageFileKey | null;
  edo_status: EmployeeEdoStatus;
  sign_type: EmployeeSignType;
  onboarding_attempt?: EmployeeOnboardingAttempt | null;
};
export type PageEmployeeRetrieveSerializer = {
  page_num: number;
  page_size: number;
  has_prev: boolean;
  has_next: boolean;
  total_items: number | null;
  total_pages: number | null;
  items: EmployeeRetrieveSerializer[];
};
export type ResponseModelPageEmployeeRetrieveSerializer = {
  detail: PageEmployeeRetrieveSerializer;
};
export type ResponseModelEmployeeRetrieveSerializer = {
  detail: EmployeeRetrieveSerializer;
};
export type EmployeeWithIpnExistsErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type WorkEmailIsRequiredErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type EmployeeCreateSerializer = {
  ipn: string | string | string;
  email?: string | null;
  work_email?: string | null;
  phone?: string | null;
  position?: string | null;
  date_joined?: string | null;
  date_of_birth?: string | null;
  company_number?: string | null;
  company_id: string;
  leader_id?: string | null;
  managerial_leader_id?: string | null;
  group_id?: string | null;
  /** Ім'я. Повинно починатися з великої літери. */
  first_name: string;
  /** Прізвище. Повинно починатися з великої літери. */
  last_name: string;
  /** По батькові. Повинно починатися з великої літери. */
  middle_name: string;
  sign_type?: EmployeeSignType;
  status?: 'candidate' | null;
};
export type ActionEmployeeFilterParams = {
  all?: boolean;
  id__in?: string[] | null;
  id__not_in?: string[] | null;
  company__in?: string[] | null;
  status__in?: EmployeeStatus[] | null;
  group__in?: string[] | null;
  leader__in?: string[] | null;
  sign_type__in?: EmployeeSignType[] | null;
  ipn__in?: (string | string | string)[] | null;
  position__in?: string[] | null;
  phone?: string | null;
  search?: string | null;
};
export type EmployeesDeletionResult = {
  result: boolean;
  params: ActionEmployeeFilterParams;
};
export type ResponseModelEmployeesDeletionResult = {
  detail: EmployeesDeletionResult;
};
export type ExtendedFilterGrouping = {
  company_id?: string | null;
};
export type ExtendedFilterValue = {
  friendly_name: string | null;
  value: any;
  grouping?: ExtendedFilterGrouping | null;
};
export type FilterOption = {
  field: string;
  values: (any | ExtendedFilterValue)[];
  value_type: FilterValueType;
};
export type AvailableFilters = {
  filters: FilterOption[];
};
export type ResponseModelAvailableFilters = {
  detail: AvailableFilters;
};
export type EmployeesCountResult = {
  total_number: number;
  params: ActionEmployeeFilterParams;
};
export type ResponseModelEmployeesCountResult = {
  detail: EmployeesCountResult;
};
export type EmployeeValidateSerializer = {
  ipn: string | string | string;
  email?: string | null;
  work_email?: string | null;
  phone?: string | null;
  position?: string | null;
  date_joined?: string | null;
  date_of_birth?: string | null;
  company_number?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  middle_name?: string | null;
  pib: string;
  leader?: string | null;
  managerial_leader?: string | null;
  group?: string | null;
  edo_role?: string | null;
};
export type NonDraftEmployeeUpdateErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type EmployeeChangePermissionErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type EmployeeUpdateSerializer = {
  ipn: string | string | string;
  email?: string | null;
  work_email?: string | null;
  phone?: string | null;
  position?: string | null;
  date_joined?: string | null;
  date_of_birth?: string | null;
  company_number?: string | null;
  leader_id?: string | null;
  managerial_leader_id?: string | null;
  group_id?: string | null;
  /** Ім'я. Повинно починатися з великої літери. */
  first_name: string;
  /** Прізвище. Повинно починатися з великої літери. */
  last_name: string;
  /** По батькові. Повинно починатися з великої літери. */
  middle_name: string;
};
export type PartialEmployeeUpdateSerializer = {
  ipn?: string | string | string | null;
  email?: string | null;
  work_email?: string | null;
  phone?: string | null;
  position?: string | null;
  date_joined?: string | null;
  date_of_birth?: string | null;
  company_number?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  middle_name?: string | null;
  leader_id?: string | null;
  managerial_leader_id?: string | null;
  group_id?: string | null;
  status?: ('candidate' | 'draft' | 'archived') | null;
};
export type AlreadyArchivedEmployeeErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type EmployeesInviteResult = {
  result: boolean;
  params: ActionEmployeeFilterParams;
};
export type ResponseModelEmployeesInviteResult = {
  detail: EmployeesInviteResult;
};
export type BodyUpdateEmployeePhoto = {
  image: Blob;
};
export type MonthlyChange = {
  /** Дата в форматі 'YYYY-MM-DD' */
  date: string;
  prev_allowed_days: string | null;
  new_allowed_days?: string | null;
};
export type Absence = {
  period_id: string;
  absence_type: AbsenceType;
  allowed_days: string | null;
  left_days: string | null;
  monthly_changes?: MonthlyChange[] | null;
  id: string;
};
export type ResponseModelAbsence = {
  detail: Absence;
};
export type InvalidTaskDatesErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type CreateAbsenceSerializer = {
  absence_type: AbsenceType;
  /** Дата в форматі 'YYYY-MM-DD' */
  date_start: string;
  allowed_days?: (number | string) | null;
  left_days?: (number | string) | null;
};
export type TaskTemplate = {
  id: string;
  created_by_id: string;
  version_identifier: string;
  version: number;
  name: string;
  mobile_name: string | null;
  document_name: string | null;
  period_type: TaskPeriodType;
  status: TaskTemplateStatus;
  type: TaskTemplateType;
  company_id: string;
  default_manager_id: string | null;
};
export type AbsenceUsage = {
  used_days: string;
  task_id: string | null;
  absence_id: string;
  id: string;
};
export type FullAbsence = {
  period_id: string;
  absence_type: AbsenceType;
  allowed_days: string | null;
  left_days: string | null;
  monthly_changes?: MonthlyChange[] | null;
  id: string;
  task_template: TaskTemplate | null;
  used?: string | null;
  usages: AbsenceUsage[];
};
export type FullWorkingPeriodAbsence = {
  id: string;
  employee_id: string;
  /** Дата в форматі 'YYYY-MM-DD' */
  date_start: string;
  /** Дата в форматі 'YYYY-MM-DD' */
  date_end: string;
  absences: FullAbsence[];
};
export type PageFullWorkingPeriodAbsence = {
  page_num: number;
  page_size: number;
  has_prev: boolean;
  has_next: boolean;
  total_items: number | null;
  total_pages: number | null;
  items: FullWorkingPeriodAbsence[];
};
export type ResponseModelPageFullWorkingPeriodAbsence = {
  detail: PageFullWorkingPeriodAbsence;
};
export type EmployeeReadPermissionErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type UpdateAbsenceSerializer = {
  allowed_days?: (number | string) | null;
  left_days?: (number | string) | null;
  apply_from?: string | null;
};
export type WorkingPeriod = {
  employee_id: string;
  /** Дата в форматі 'YYYY-MM-DD' */
  date_start: string;
  date_end?: string | null;
  id: string;
};
export type ResponseModelWorkingPeriod = {
  detail: WorkingPeriod;
};
export type UpdateWorkingPeriodSerializer = {
  date_start?: string | null;
  date_end?: string | null;
};
export type TaskAttachment = {
  id: string;
  employee_id: string;
  media_file_id: string;
  attachment_type?: TaskAttachmentType | null;
};
export type ResponseModelTaskAttachment = {
  detail: TaskAttachment;
};
export type BodyEmployeeUploadAttachment = {
  file: Blob;
};
export type MediaFile = {
  /** Ключ файлу на S3 */
  file_key: string;
  type: MediaFileType;
  uploader_id?: string | null;
  uploader_employee_id?: string | null;
  antivirus_check_status?: AntivirusCheckStatus;
  id: string;
};
export type FullTaskAttachment = {
  id: string;
  employee_id: string;
  media_file_id: string;
  attachment_type?: TaskAttachmentType | null;
  media_file: MediaFile;
};
export type PageFullTaskAttachment = {
  page_num: number;
  page_size: number;
  has_prev: boolean;
  has_next: boolean;
  total_items: number | null;
  total_pages: number | null;
  items: FullTaskAttachment[];
};
export type ResponseModelPageFullTaskAttachment = {
  detail: PageFullTaskAttachment;
};
export type EmployeeAlreadyInvitedErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type EmployeeInvalidStatusErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type UpdateSigningTypeSerializer = {
  sign_type: EmployeeSignType;
};
export type EmployeeDocumentRetrieveSerializer = {
  id: string;
  employee_id: string;
  document_type: EmployeeDocumentType;
  media_file_id: string;
  /** Дата з часом у форматі 'YYYY-MM-DDTHH:MM:SS' */
  date_created: string;
  media_file_key: string;
};
export type PageEmployeeDocumentRetrieveSerializer = {
  page_num: number;
  page_size: number;
  has_prev: boolean;
  has_next: boolean;
  total_items: number | null;
  total_pages: number | null;
  items: EmployeeDocumentRetrieveSerializer[];
};
export type ResponseModelPageEmployeeDocumentRetrieveSerializer = {
  detail: PageEmployeeDocumentRetrieveSerializer;
};
export type ResponseModelGroupRetrieveSerializer = {
  detail: GroupRetrieveSerializer;
};
export type GroupCreateSerializer = {
  name: string;
  company_id: string;
};
export type PageGroupRetrieveSerializer = {
  page_num: number;
  page_size: number;
  has_prev: boolean;
  has_next: boolean;
  total_items: number | null;
  total_pages: number | null;
  items: GroupRetrieveSerializer[];
};
export type ResponseModelPageGroupRetrieveSerializer = {
  detail: PageGroupRetrieveSerializer;
};
export type GroupUpdateSerializer = {
  name: string;
};
export type TaskResponseModel = {
  task_id: string;
};
export type ResponseModelTaskResponseModel = {
  detail: TaskResponseModel;
};
export type BodyCreateFileImportUpload = {
  file: Blob;
};
export type FileImportTask = {
  id: string;
  type: ImportTaskType;
  status: ImportTaskStatus;
  media_file_id?: string | null;
  execution_details: {
    [key: string]: any;
  };
  company_id: string;
  created_by_id: string;
};
export type ResponseModelFileImportTask = {
  detail: FileImportTask;
};
export type PdfTextPrintBlock = {
  x: number;
  y: number;
  width: number;
  height: number;
  type?: 'text';
  text: string;
  font_size?: number;
  font_name?: string;
  align?: PdfBlockAlign;
  vertical_align?: PdfBlockVerticalAlign;
};
export type PdfPrintGroup = {
  x: number;
  y: number;
  width: number;
  height: number;
  type?: 'group';
  blocks: (PdfPrintGroup | PdfTextPrintBlock)[];
};
export type PdfPageDetails = {
  print_blocks: (PdfPrintGroup | PdfTextPrintBlock)[];
};
export type PdfFileDetails = {
  pages: PdfPageDetails[];
};
export type TaskDocumentGenerationParamsSerializer = {
  pdf_details?: PdfFileDetails | null;
};
export type FileDocumentInfoSerializer = {
  name?: string | null;
  document_number?: string | null;
  category?: HrsDocumentCategory | null;
};
export type VacationTaskTemplateDocumentActionRetrieveSerializer = {
  id: string;
  order: number;
  action_type: DocumentActionType;
  executor_type: ActionExecutorType;
  executor_id: string | null;
};
export type VacationTaskTemplateDocumentRetrieveSerializer = {
  id: string;
  order: number;
  document_type: TaskDocumentType;
  file_document_id: string | null;
  file_document_params?: TaskDocumentGenerationParamsSerializer | null;
  document_info?: FileDocumentInfoSerializer | null;
  grouping_id?: string | null;
  is_parallel_action: boolean;
  access_settings: TaskDocumentAccessSettings;
  actions: VacationTaskTemplateDocumentActionRetrieveSerializer[];
};
export type VacationTaskTemplateRetrieveSerializer = {
  id: string;
  version_identifier: string;
  version: number;
  name: string;
  status: TaskTemplateStatus;
  type: TaskTemplateType;
  period_type: TaskPeriodType;
  company_id: string;
  default_manager_id: string | null;
  documents: VacationTaskTemplateDocumentRetrieveSerializer[];
};
export type ResponseModelVacationTaskTemplateRetrieveSerializer = {
  detail: VacationTaskTemplateRetrieveSerializer;
};
export type PdfPrintGroup2 = {
  x: number;
  y: number;
  width: number;
  height: number;
  type?: 'group';
  blocks: (PdfPrintGroup2 | PdfTextPrintBlock)[];
};
export type PdfPageDetails2 = {
  print_blocks: (PdfPrintGroup2 | PdfTextPrintBlock)[];
};
export type PdfFileDetails2 = {
  pages: PdfPageDetails2[];
};
export type TaskDocumentGenerationParamsSerializer2 = {
  pdf_details?: PdfFileDetails2 | null;
};
export type VacationTaskTemplateDocumentActionCreateSerializer = {
  action_type: DocumentActionType;
  executor_type: ActionExecutorType;
  executor_id?: string | null;
};
export type VacationTaskTemplateDocumentCreateSerializer = {
  is_parallel_action: boolean;
  document_type: TaskDocumentType;
  file_document_id?: string | null;
  file_document_params?: TaskDocumentGenerationParamsSerializer2 | null;
  actions: VacationTaskTemplateDocumentActionCreateSerializer[];
  access_settings?: TaskDocumentAccessSettings;
};
export type VacationTaskTemplateCreateSerializer = {
  type: TaskTemplateType;
  name: string;
  period_type?: TaskPeriodType;
  documents: VacationTaskTemplateDocumentCreateSerializer[];
  default_manager_id?: string | null;
  company_id: string;
};
export type VacationTaskTemplateDocumentPreviewSerializer = {
  is_parallel_action: boolean;
  document_type: TaskDocumentType;
  file_document_id?: string | null;
  file_document_params?: TaskDocumentGenerationParamsSerializer2 | null;
  actions: VacationTaskTemplateDocumentActionCreateSerializer[];
  access_settings?: TaskDocumentAccessSettings;
  company_id: string;
  type: TaskTemplateType;
};
export type InvalidTemplateActivityStatusErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type VacationTaskTemplateUpdateSerializer = {
  type: TaskTemplateType;
  name: string;
  period_type?: TaskPeriodType;
  documents: VacationTaskTemplateDocumentCreateSerializer[];
  default_manager_id?: string | null;
};
export type InvalidManagerErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type VacationTaskTemplateManagerSerializer = {
  default_manager_id: string;
};
export type SendDocumentPeriodDatesWithDays = {
  start_date: string;
  end_date: string;
  days_available?: string | null;
  allowed_days?: string | null;
};
export type AvailablePeriodDatesSerializer = {
  dates: SendDocumentPeriodDatesWithDays[];
};
export type ResponseModelAvailablePeriodDatesSerializer = {
  detail: AvailablePeriodDatesSerializer;
};
export type UserNameInformation = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  middle_name?: string | null;
};
export type MinimalCompany = {
  id: string;
  name: string;
  color?: string | null;
};
export type TaskTemplateListSerializer = {
  version_identifier: string;
  version: number;
  name: string;
  status: TaskTemplateStatus;
  type: TaskTemplateType;
  period_type: TaskPeriodType;
  created_by: UserNameInformation | null;
  company: MinimalCompany;
  default_manager: UserNameInformation | null;
  date_created: string;
  date_updated: string;
};
export type PageTaskTemplateListSerializer = {
  page_num: number;
  page_size: number;
  has_prev: boolean;
  has_next: boolean;
  total_items: number | null;
  total_pages: number | null;
  items: TaskTemplateListSerializer[];
};
export type ResponseModelPageTaskTemplateListSerializer = {
  detail: PageTaskTemplateListSerializer;
};
export type TaskFilterParams = {
  all?: boolean;
  id__in?: string[] | null;
  id__not_in?: string[] | null;
  page_num?: number;
  page_size?: number;
  company__in?: string[] | null;
  task_template__in?: string[] | null;
  status__in?: TaskStatus[] | null;
  start_date?: string | null;
  end_date?: string | null;
  manager__in?: string[] | null;
  search?: string | null;
  require_attention?: boolean | null;
};
export type TaskCountResult = {
  total_number: number;
  params: TaskFilterParams;
};
export type ResponseModelTaskCountResult = {
  detail: TaskCountResult;
};
export type SendDocumentPeriodDates = {
  start_date: string;
  end_date: string;
};
export type SendDocumentPeriod = {
  dates: SendDocumentPeriodDates[];
};
export type ExtendedFileDocumentInfoSerializer = {
  name?: string | null;
  document_number?: string | null;
  category?: HrsDocumentCategory | null;
  order_number?: string | null;
  periods?: SendDocumentPeriod | null;
  /** Чи працівник отримує матеріальну допомогу під час відпустки. */
  financial_assistance?: boolean;
};
export type TaskDocumentRetrieveSerializer = {
  id: string | null;
  task_id: string;
  task_route_id: string | null;
  document_type: TaskDocumentType | null;
  document_state: TaskRetrieveDocumentState;
  document_info: ExtendedFileDocumentInfoSerializer | null;
  name: string;
};
export type ResponseModelTaskDocumentRetrieveSerializer = {
  detail: TaskDocumentRetrieveSerializer;
};
export type TaskAccessErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type BodyTaskUploadDocument = {
  file: Blob;
};
export type PageTaskDocumentRetrieveSerializer = {
  page_num: number;
  page_size: number;
  has_prev: boolean;
  has_next: boolean;
  total_items: number | null;
  total_pages: number | null;
  items: TaskDocumentRetrieveSerializer[];
};
export type ResponseModelPageTaskDocumentRetrieveSerializer = {
  detail: PageTaskDocumentRetrieveSerializer;
};
export type OperationDetailsDocumentsToSend = {
  route_id: string;
  document_type: TaskDocumentType;
  order_number_required?: boolean | null;
  period_required?: boolean | null;
};
export type OperationDetails = {
  route_id?: string | null;
  documents_to_send?: OperationDetailsDocumentsToSend[] | null;
};
export type TaskOperation = {
  operation_type: TaskOperationType;
  operation_details: OperationDetails;
};
export type TaskOperations = {
  operations: TaskOperation[];
};
export type ResponseModelTaskOperations = {
  detail: TaskOperations;
};
export type SingleTaskError = {
  field: string;
  error: TaskErrorType;
  value: any;
};
export type CompanyErrors = {
  id: string;
  data: Company;
  errors: SingleTaskError[];
};
export type EmployeeErrors = {
  id: string;
  data: EmployeeRetrieveSerializer;
  errors: SingleTaskError[];
};
export type TaskErrorsSerializer = {
  company: CompanyErrors;
  employees: EmployeeErrors[];
};
export type ResponseModelTaskErrorsSerializer = {
  detail: TaskErrorsSerializer;
};
export type MinimalEmployeeWithAbsences = {
  id: string;
  email: string | null;
  first_name: string;
  last_name: string;
  middle_name: string;
  position: string | null;
  vacation_days: number | null;
  profile_photo: ProfileImageFileKey | null;
};
export type MinimalTaskTemplateInfo = {
  version_identifier: string;
  version: number;
  name: string;
  mobile_name: string | null;
  document_name: string | null;
  status: TaskTemplateStatus;
  type: TaskTemplateType;
  period_type: TaskPeriodType;
};
export type PositionedUserNameInformation = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  middle_name?: string | null;
  position?: string | null;
  profile_photo?: ProfileImageFileKey | null;
};
export type UserNameInformationWithPhoto = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  middle_name?: string | null;
  profile_photo: ProfileImageFileKey | null;
};
export type MinimalTaskTemplateDocument = {
  id: string;
  document_type: TaskDocumentType;
  task_template_id: string;
  file_document_id: string | null;
  order: number;
  is_parallel_action: boolean;
  access_settings?: TaskDocumentAccessSettings;
};
export type FileDocument = {
  id: string;
  name: string;
  company_id: string;
  edo_document_id: string | null;
  task_id: string | null;
  task_route_id: string | null;
  file_key: string;
  date_created: string;
  date_updated: string;
};
export type TaskTemplateDocumentAction = {
  id: string;
  task_template_document_id: string;
  order: number;
  action_type: DocumentActionType;
  executor_type: ActionExecutorType;
  executor_id: string | null;
};
export type MinimalEmployee = {
  id: string;
  email: string | null;
  first_name: string;
  last_name: string;
  middle_name: string;
  position: string | null;
};
export type MinimalUser = {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  middle_name: string | null;
};
export type FullTaskRouteAction = {
  id: string;
  task_template_document_action: TaskTemplateDocumentAction | null;
  executor: MinimalEmployee | null;
  manager: MinimalUser | null;
  order: number;
  status: TaskRouteActionStatus;
  decline_reason: string | null;
  action_type: DocumentActionType | null;
  date_created: string;
  date_updated: string;
  validity_status?: ActionValidityStatus;
};
export type FullTaskRoute = {
  id: string;
  name: string;
  task_template_document: MinimalTaskTemplateDocument | null;
  file_document: FileDocument | null;
  order: number;
  route_type: TaskRouteType;
  is_parallel_action: boolean | null;
  access_settings: TaskDocumentAccessSettings | null;
  status: TaskRouteStatus;
  actions: FullTaskRouteAction[];
  document_info: {
    [key: string]: any;
  } | null;
  grouping_id?: string | null;
  date_created: string;
  date_updated: string;
};
export type TaskRetrieveSerializer = {
  id: string;
  status: TaskStatus;
  activity_status: TaskActivityStatus;
  initiator: MinimalEmployeeWithAbsences;
  task_template: MinimalTaskTemplateInfo;
  start_date: string | null;
  end_date: string | null;
  company: MinimalCompany;
  leader: PositionedUserNameInformation | null;
  manager: UserNameInformationWithPhoto | null;
  require_attention: boolean;
  description: string | null;
  cancellation_reason: string | null;
  require_attachments: boolean;
  documents_required_number: number;
  documents_sent_number: number;
  route: FullTaskRoute[];
  documents: TaskDocumentRetrieveSerializer[];
  days_number: number;
};
export type ResponseModelTaskRetrieveSerializer = {
  detail: TaskRetrieveSerializer;
};
export type TaskCreateSerializer = {
  company_id: string;
  task_template_version_identifier: string;
  initiator_id: string;
  start_date?: string | null;
  end_date?: string | null;
};
export type TaskListSerializer = {
  id: string;
  status: TaskStatus;
  activity_status: TaskActivityStatus;
  initiator: MinimalEmployeeWithAbsences;
  task_template: MinimalTaskTemplateInfo;
  start_date: string | null;
  end_date: string | null;
  company: MinimalCompany;
  leader: PositionedUserNameInformation | null;
  manager: UserNameInformationWithPhoto | null;
  require_attention: boolean;
  description: string | null;
  cancellation_reason: string | null;
  require_attachments: boolean;
  documents_required_number: number;
  documents_sent_number: number;
};
export type PageTaskListSerializer = {
  page_num: number;
  page_size: number;
  has_prev: boolean;
  has_next: boolean;
  total_items: number | null;
  total_pages: number | null;
  items: TaskListSerializer[];
};
export type ResponseModelPageTaskListSerializer = {
  detail: PageTaskListSerializer;
};
export type InvalidTaskTypeErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type InvalidTaskStatusErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type TaskUpdateSerializer = {
  start_date: string;
  end_date: string;
  manager_id: string | null;
  require_attachments: boolean;
};
export type TaskCancelSerializer = {
  cancellation_reason: string;
};
export type InvalidTaskRouteIdErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type InvalidTaskRouteStatusErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type SendTaskDocumentSerializer = {
  route_id: string;
  order_number?: string | null;
  periods?: SendDocumentPeriod | null;
  /** Чи працівник отримує матеріальну допомогу під час відпустки. */
  financial_assistance?: boolean;
};
export type SendTaskDocumentsSerializer = {
  documents_to_send: SendTaskDocumentSerializer[];
};
export type BodyTaskUploadAttachment = {
  file: Blob;
};
export type TaskRouteDocumentErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type TaskRouteDocumentActionUpdateSerializer = {
  executor_id: string;
  action_type: DocumentActionType;
};
export type TaskRouteDocumentUpdateSerializer = {
  actions: TaskRouteDocumentActionUpdateSerializer[];
  file_document_id: string;
  document_info?: FileDocumentInfoSerializer | null;
  grouping_id?: string | null;
  access_settings?: TaskDocumentAccessSettings | null;
  is_parallel_action?: boolean | null;
};
export type TaskFullRouteUpdateSerializer = {
  route: TaskRouteDocumentUpdateSerializer[];
};
export type TaskRouteEditErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type UserReadPermissionErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type TaskRouteActionUpdateSerializer = {
  task_template_document_action_id: string | null;
  executor_id: string | null;
  manager_id: string | null;
  action_type: DocumentActionType;
};
export type TaskRouteUpdateSerializer = {
  actions: TaskRouteActionUpdateSerializer[];
  access_settings?: TaskDocumentAccessSettings | null;
  is_parallel_action?: boolean | null;
};
export type RemindToActionResponseSerializer = {
  status: boolean;
};
export type ResponseModelRemindToActionResponseSerializer = {
  detail: RemindToActionResponseSerializer;
};
export type EmployeeVchasnoIdErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type MediaFileNotFoundErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type CreatedPublicApiTokenSerializer = {
  token: string;
  id: string;
  company_id: string;
  active_status: PublicTokenActiveStatus;
  date_created: string;
};
export type ResponseModelCreatedPublicApiTokenSerializer = {
  detail: CreatedPublicApiTokenSerializer;
};
export type CreatePublicApiToken = {
  company_id: string;
};
export type PublicTokenRetrieveSerializer = {
  id: string;
  company_id: string;
  active_status: PublicTokenActiveStatus;
  date_created: string;
};
export type PublicTokenListSerializer = {
  public_tokens: PublicTokenRetrieveSerializer[];
};
export type ResponseModelPublicTokenListSerializer = {
  detail: PublicTokenListSerializer;
};
export type ResponseModelPublicTokenRetrieveSerializer = {
  detail: PublicTokenRetrieveSerializer;
};
export type PublicTokenActivationErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type UpdatePublicApiTokenSerializer = {
  active_status: PublicTokenActiveStatus;
};
export type EmployeeOnboardingAttemptReceiveSerializer = {
  id: string;
  company_id: string;
  invite_link: string;
};
export type ResponseModelEmployeeOnboardingAttemptReceiveSerializer = {
  detail: EmployeeOnboardingAttemptReceiveSerializer;
};
export type EmployeeOnboardingDiiaNotAvailableErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type EmployeeOnboardingRateLimitExceededErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type EmployeeOnboardingDiiaNotConfiguredErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type EmployeeOnboardingPhoneAlreadyExistsErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type EmployeeOnboardingAttemptCreateInvalidErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type CreateEmployeeOnboardingAttemptSerializer = {
  company_id: string;
  employee_phone?: string | null;
  full_name?: string | null;
  employee_id?: string | null;
};
export type EmployeeOnboardingInvalidStatusErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type UpdateEmployeeOnboardingAttemptSerializer = {
  status?: EmployeeOnboardingAttemptStatus | null;
};
export type EmployeeOnboardingDeeplinkReceiveSerializer = {
  deeplink: string;
  received_at: string;
};
export type ResponseModelEmployeeOnboardingDeeplinkReceiveSerializer = {
  detail: EmployeeOnboardingDeeplinkReceiveSerializer;
};
export type EmployeeOnboardingDiiaInvalidErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type EmployeeOnboardingAttemptCompletedErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type EmployeeWithProfileImage = {
  id: string;
  last_name: string;
  first_name?: string | null;
  middle_name?: string | null;
  ipn?: string | string | string | null;
  email?: string | null;
  work_email?: string | null;
  phone?: string | null;
  position?: string | null;
  date_joined?: string | null;
  date_of_birth?: string | null;
  company_number?: string | null;
  group_id?: string | null;
  leader_id?: string | null;
  managerial_leader_id?: string | null;
  company_id: string;
  status: EmployeeStatus;
  photo_media_file_id?: string | null;
  sign_type?: EmployeeSignType;
  invite_sent_at?: string | null;
  profile_photo?: ProfileImageFileKey | null;
};
export type Group = {
  id: string;
  name: string;
  company_id: string;
  leader_id?: string | null;
};
export type FullEmployee = {
  id: string;
  ipn: string | string | string | null;
  email: string | null;
  work_email: string | null;
  first_name: string | null;
  last_name: string | null;
  middle_name: string | null;
  phone: string | null;
  position: string | null;
  date_joined: string | null;
  date_of_birth: string | null;
  company_number: string | null;
  status: EmployeeStatus;
  sign_type: EmployeeSignType;
  leader: EmployeeWithProfileImage | null;
  managerial_leader: EmployeeWithProfileImage | null;
  group: Group | null;
  company: Company;
  vacation_days: number | null;
  profile_image?: ProfileImageFileKey | null;
  invite_sent_at?: string | null;
};
export type VchasnoEmployeesSerializer = {
  employees: FullEmployee[];
};
export type ResponseModelVchasnoEmployeesSerializer = {
  detail: VchasnoEmployeesSerializer;
};
export type ResponseModelFullEmployee = {
  detail: FullEmployee;
};
export type EmployeeTaskTemplate = {
  id: string;
  version_identifier: string;
  version: number;
  name: string;
  mobile_name: string | null;
  status: TaskTemplateStatus;
  type: TaskTemplateType;
  period_type: TaskPeriodType;
  company_id: string;
  date_created: string;
  date_updated: string;
  absence: Absence | null;
};
export type PageEmployeeTaskTemplate = {
  page_num: number;
  page_size: number;
  has_prev: boolean;
  has_next: boolean;
  total_items: number | null;
  total_pages: number | null;
  items: EmployeeTaskTemplate[];
};
export type ResponseModelPageEmployeeTaskTemplate = {
  detail: PageEmployeeTaskTemplate;
};
export type Task = {
  id: string;
  task_template_id: string;
  company_id: string;
  manager_id: string | null;
  initiator_id: string;
  leader_id: string | null;
  status: TaskStatus;
  activity_status: TaskActivityStatus;
  start_date: string | null;
  end_date: string | null;
  description: string | null;
  cancellation_reason: string | null;
  require_attachments?: boolean;
  date_created: string;
  date_updated: string;
};
export type ResponseModelTask = {
  detail: Task;
};
export type EmployeeTaskCreateSerializer = {
  task_template_version_identifier: string;
  start_date: string;
  end_date: string;
};
export type EmployeeTaskUpdateSerializer = {
  description: string | null;
};
export type EmployeeTaskFull = {
  id: string;
  status: TaskStatus;
  activity_status: TaskActivityStatus;
  start_date: string;
  end_date: string;
  description: string | null;
  task_template: MinimalTaskTemplateInfo;
  initiator: PositionedUserNameInformation;
  manager: UserNameInformation | null;
  leader: PositionedUserNameInformation | null;
  cancellation_reason: string | null;
  require_attachments: boolean;
  task_routes: FullTaskRoute[];
};
export type PageEmployeeTaskFull = {
  page_num: number;
  page_size: number;
  has_prev: boolean;
  has_next: boolean;
  total_items: number | null;
  total_pages: number | null;
  items: EmployeeTaskFull[];
};
export type ResponseModelPageEmployeeTaskFull = {
  detail: PageEmployeeTaskFull;
};
export type ResponseModelEmployeeTaskFull = {
  detail: EmployeeTaskFull;
};
export type EmployeeTaskFilterParams = {
  all?: boolean;
  id__in?: string[] | null;
  id__not_in?: string[] | null;
  page_num?: number;
  page_size?: number;
  status__in?: TaskStatus[] | null;
  search?: string | null;
  route_status__in?: TaskRouteStatus[] | null;
};
export type CountEmployeeTaskToActionSerializer = {
  count: number;
  params: EmployeeTaskFilterParams;
};
export type ResponseModelCountEmployeeTaskToActionSerializer = {
  detail: CountEmployeeTaskToActionSerializer;
};
export type TaskApprovalErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type LeaderTaskRouteActionStatusChangeSerializer = {
  decline_reason: string;
};
export type Name = {
  full_name: string | null;
  short_name: string | null;
};
export type NameInEnglish = {
  full_name: string | null;
  short_name: string | null;
};
export type Address = {
  zip: string | null;
  country: string | null;
  region: string | null;
  district: string | null;
  city: string | null;
  city_type: string | null;
  street: string | null;
  street_type: string | null;
  building: string | null;
  sub_building: string | null;
  sub_building_type: string | null;
  apartment: string | null;
  apartment_type: string | null;
  atu: string | null;
  atu_code: string | null;
};
export type Signer = {
  name: string | null;
  role: string | null;
  description: string | null;
  appoint_date: string | string | null;
  raw: string | null;
};
export type LegalPersonRegisterData = {
  name: Name | string;
  name_in_english?: NameInEnglish | null;
  address?: string | null;
  address_parts?: Address | null;
  signers?: Signer[] | null;
};
export type InternalCompany = {
  edrpou: string | string | string;
  name: string | null;
  color: string | null;
  leader_id: string | null;
  max_employees_count: number;
  legal_info?: LegalPersonRegisterData | null;
  plan?: CompanyPlanType;
  id: string;
};
export type ResponseModelInternalCompany = {
  detail: InternalCompany;
};
export type AdminAccessErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type CompanyMaxEmployeesUpdateSerializer = {
  /** Max number of employees in the company. */
  max_employees: number;
};
export type PageInternalCompany = {
  page_num: number;
  page_size: number;
  has_prev: boolean;
  has_next: boolean;
  total_items: number | null;
  total_pages: number | null;
  items: InternalCompany[];
};
export type ResponseModelPageInternalCompany = {
  detail: PageInternalCompany;
};
export type ChangePlanSerializer = {
  plan: CompanyPlanType;
  admin_email?: string | null;
};
export type InternalUser = {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  status: UserStatus;
  middle_name?: string | null;
  vchasno_id?: string | null;
  photo_media_file_id?: string | null;
  locale?: Language;
  profile_image?: ProfileImageFileKey | null;
  internal_role: InternalUserRole;
};
export type ResponseModelInternalUser = {
  detail: InternalUser;
};
export type UpdateInternalUserRoleSerializer = {
  internal_role: InternalUserRole;
};
export type RolePermissionsSchema = {
  can_comment_document?: boolean;
  can_sign_and_reject_document?: boolean;
  can_delete_document?: boolean;
  can_upload_document?: boolean;
  can_invite_coworkers?: boolean;
  can_view_coworkers?: boolean;
};
export type AddEdoRoleSerializer = {
  email: string;
  edrpou: string;
  position: string;
  permissions?: RolePermissionsSchema | null;
};
export type RemoveEdoRoleSerializer = {
  vchasno_id: string;
  edrpou: string;
};
export type BodyAdminUploadTaskTemplateDocument = {
  file: Blob;
};
export type PublicApiEmployeeRetrieveSerializer = {
  id: string;
  /** Прізвище. Повинно починатися з великої літери. */
  last_name: string;
  first_name?: string | null;
  middle_name?: string | null;
  ipn?: string | string | string | null;
  email?: string | null;
  work_email?: string | null;
  phone?: string | null;
  position?: string | null;
  date_joined?: string | null;
  date_of_birth?: string | null;
  company_number?: string | null;
  company_id: string;
  status: EmployeeStatus;
  sign_type: EmployeeSignType;
  leader_id: string | null;
  managerial_leader_id: string | null;
  group_id: string | null;
};
export type ResponseModelPublicApiEmployeeRetrieveSerializer = {
  detail: PublicApiEmployeeRetrieveSerializer;
};
export type PublicApiCreateEmployeeSerializer = {
  /** Ім'я. Повинно починатися з великої літери. */
  first_name: string;
  /** Прізвище. Повинно починатися з великої літери. */
  last_name: string;
  /** По батькові. Повинно починатися з великої літери. */
  middle_name: string;
  ipn?: string | string | string | null;
  email?: string | null;
  work_email?: string | null;
  phone?: string | null;
  position?: string | null;
  date_joined?: string | null;
  date_of_birth?: string | null;
  company_number?: string | null;
  status?: EmployeeStatus;
  sign_type?: EmployeeSignType;
  leader_id?: string | null;
  managerial_leader_id?: string | null;
  group_id?: string | null;
};
export type PagePublicApiEmployeeRetrieveSerializer = {
  page_num: number;
  page_size: number;
  has_prev: boolean;
  has_next: boolean;
  total_items: number | null;
  total_pages: number | null;
  items: PublicApiEmployeeRetrieveSerializer[];
};
export type ResponseModelPagePublicApiEmployeeRetrieveSerializer = {
  detail: PagePublicApiEmployeeRetrieveSerializer;
};
export type PublicApiUpdateEmployeeSerializer = {
  first_name?: string | null;
  last_name?: string | null;
  middle_name?: string | null;
  ipn?: string | string | string | null;
  email?: string | null;
  work_email?: string | null;
  phone?: string | null;
  position?: string | null;
  date_joined?: string | null;
  date_of_birth?: string | null;
  company_number?: string | null;
  status?: EmployeeStatus | null;
  sign_type?: EmployeeSignType | null;
  leader_id?: string | null;
  managerial_leader_id?: string | null;
  group_id?: string | null;
};
export type PublicApiGroupRetrieveSerializer = {
  id: string;
  name: string;
  leader_id?: string | null;
};
export type PagePublicApiGroupRetrieveSerializer = {
  page_num: number;
  page_size: number;
  has_prev: boolean;
  has_next: boolean;
  total_items: number | null;
  total_pages: number | null;
  items: PublicApiGroupRetrieveSerializer[];
};
export type ResponseModelPagePublicApiGroupRetrieveSerializer = {
  detail: PagePublicApiGroupRetrieveSerializer;
};
export type ResponseModelPublicApiGroupRetrieveSerializer = {
  detail: PublicApiGroupRetrieveSerializer;
};
export type PublicApiCreateGroupSerializer = {
  name: string;
  leader_id?: string | null;
};
export type PublicApiCompanyAccessErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type PublicApiUpdateGroupSerializer = {
  name?: string | null;
  leader_id?: string | null;
};
export type PublicApiGroupDeleteSerializer = {
  status: boolean;
};
export type ResponseModelPublicApiGroupDeleteSerializer = {
  detail: PublicApiGroupDeleteSerializer;
};
export type PublicApiListVacationDocumentSerializer = {
  id: string;
  name: string;
  type: PublicDocumentType;
  status: TaskRouteStatus;
};
export type PagePublicApiListVacationDocumentSerializer = {
  page_num: number;
  page_size: number;
  has_prev: boolean;
  has_next: boolean;
  total_items: number | null;
  total_pages: number | null;
  items: PublicApiListVacationDocumentSerializer[];
};
export type ResponseModelPagePublicApiListVacationDocumentSerializer = {
  detail: PagePublicApiListVacationDocumentSerializer;
};
export type PublicApiRetrieveDocumentSignerSerializer = {
  executor_id: string;
  status: TaskRouteActionStatus;
};
export type PagePublicApiRetrieveDocumentSignerSerializer = {
  page_num: number;
  page_size: number;
  has_prev: boolean;
  has_next: boolean;
  total_items: number | null;
  total_pages: number | null;
  items: PublicApiRetrieveDocumentSignerSerializer[];
};
export type ResponseModelPagePublicApiRetrieveDocumentSignerSerializer = {
  detail: PagePublicApiRetrieveDocumentSignerSerializer;
};
export type ResponseModelPublicApiRetrieveDocumentSignerSerializer = {
  detail: PublicApiRetrieveDocumentSignerSerializer;
};
export type PublicApiCreateDocumentSignerSerializer = {
  executor_id: string;
};
export type PublicApiRetrieveVacationSerializer = {
  id: string;
  /** ID співробітника, що ініціює запит. У випадку відпустки, це співробітник, що йде у відпустку. */
  initiator_id: string;
  template_version_identifier: string;
  status: TaskStatus;
  date_start: string | null;
  date_end: string | null;
  description: string | null;
  cancellation_reason: string | null;
};
export type ResponseModelPublicApiRetrieveVacationSerializer = {
  detail: PublicApiRetrieveVacationSerializer;
};
export type SendVacationDocumentSerializer = {
  document_id: string;
  order_number?: string | null;
  periods?: SendDocumentPeriod | null;
  /** Чи працівник отримує матеріальну допомогу під час відпустки. */
  financial_assistance?: boolean;
};
export type SendVacationDocumentsSerializer = {
  documents_to_send: SendVacationDocumentSerializer[];
};
export type PublicApiRetrieveUniversalDocumentSerializer = {
  id: string;
  /** ID співробітника, що ініціює запит. У випадку відпустки, це співробітник, що йде у відпустку. */
  initiator_id: string;
  status: TaskStatus;
  description: string | null;
  cancellation_reason: string | null;
};
export type PagePublicApiRetrieveUniversalDocumentSerializer = {
  page_num: number;
  page_size: number;
  has_prev: boolean;
  has_next: boolean;
  total_items: number | null;
  total_pages: number | null;
  items: PublicApiRetrieveUniversalDocumentSerializer[];
};
export type ResponseModelPagePublicApiRetrieveUniversalDocumentSerializer = {
  detail: PagePublicApiRetrieveUniversalDocumentSerializer;
};
export type PublicApiListUniversalDocumentDocumentSerializer = {
  id: string;
  name: string;
  status: TaskRouteStatus;
  document_info: ExtendedFileDocumentInfoSerializer | null;
};
export type PagePublicApiListUniversalDocumentDocumentSerializer = {
  page_num: number;
  page_size: number;
  has_prev: boolean;
  has_next: boolean;
  total_items: number | null;
  total_pages: number | null;
  items: PublicApiListUniversalDocumentDocumentSerializer[];
};
export type ResponseModelPagePublicApiListUniversalDocumentDocumentSerializer =
  {
    detail: PagePublicApiListUniversalDocumentDocumentSerializer;
  };
export type ResponseModelPublicApiRetrieveUniversalDocumentSerializer = {
  detail: PublicApiRetrieveUniversalDocumentSerializer;
};
export type SendUniversalDocumentSerializer = {
  document_id: string;
};
export type SendUniversalDocumentsSerializer = {
  documents_to_send: SendUniversalDocumentSerializer[];
};
export type SuccessResponse = {
  success?: boolean;
};
export type BodyHandleFilesBatchUpload = {
  encode_data: string;
  multi_sharing: Blob;
};
export type EmployeeOnboardingOtpSendSerializer = {
  attempt_id: string;
  sent?: boolean;
};
export type ResponseModelEmployeeOnboardingOtpSendSerializer = {
  detail: EmployeeOnboardingOtpSendSerializer;
};
export type EmployeeOnboardingOtpRateLimitExceededErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type EmployeeOnboardingTokenSerializer = {
  token: string;
  attempt_id: string;
};
export type ResponseModelEmployeeOnboardingTokenSerializer = {
  detail: EmployeeOnboardingTokenSerializer;
};
export type EmployeeOnboardingOtpRequiredErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type EmployeeOnboardingOtpInvalidErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type EmployeeOnboardingOtpAttemptsExceededErrorResponseModel = {
  error_code: string;
  detail: string;
  error_message?: string | null;
};
export type EmployeeOnboardingAuthSerializer = {
  otp_code?: string | null;
};
export enum Language {
  Uk = 'uk',
  En = 'en',
}
export enum EmailTemplate {
  CompanyInvite = 'company_invite',
  EmployeeSignupInvite = 'employee_signup_invite',
  RemovedFromCompany = 'removed_from_company',
  NewTaskCreated = 'new_task_created',
  TaskCanceled = 'task_canceled',
}
export enum NotificationActiveStatus {
  Active = 'active',
  Inactive = 'inactive',
}
export enum UserStatus {
  Pending = 'pending',
  Active = 'active',
  Deleted = 'deleted',
}
export enum UserCompanyRole {
  Admin = 'admin',
  Manager = 'manager',
  Employee = 'employee',
}
export enum UserRoleActiveStatus {
  Active = 'active',
  Inactive = 'inactive',
  Deleted = 'deleted',
}
export enum EmployeeStatus {
  Candidate = 'candidate',
  Draft = 'draft',
  Working = 'working',
  Fired = 'fired',
  Archived = 'archived',
}
export enum EmployeeSignType {
  Personal = 'personal',
  Corporate = 'corporate',
}
export enum CompanyCheckListItemProgressStatus {
  Created = 'created',
  Done = 'done',
}
export enum CompanyCheckListItemType {
  AddEmployees = 'add_employees',
  AddEmployeesVocationsDays = 'add_employees_vocations_days',
  SetUpTasks = 'set_up_tasks',
}
export enum CompanyInviteStatus {
  Created = 'created',
  Accepted = 'accepted',
  Revoked = 'revoked',
}
export enum EmployeeEdoStatus {
  NotExist = 'not_exist',
  Inactive = 'inactive',
  Active = 'active',
  Invited = 'invited',
  HasApp = 'has_app',
}
export enum EmployeeOnboardingAttemptStatus {
  New = 'new',
  InProgress = 'in_progress',
  Completed = 'completed',
  Canceled = 'canceled',
}
export enum FilterValueType {
  Scalar = 'scalar',
  List = 'list',
}
export enum AbsenceType {
  Annual = 'annual',
  AnnualHazardous = 'annual_hazardous',
  AnnualSpecialWork = 'annual_special_work',
  SocialMaternityLeave = 'social_maternity_leave',
  SocialChildCare = 'social_child_care',
  SocialParental = 'social_parental',
  SocialChildBirth = 'social_child_birth',
  SocialAdoption = 'social_adoption',
  StudyEducational = 'study_educational',
  VacationSocialPco = 'vacation_social_pco',
  StudyCreative = 'study_creative',
  StudyAthletic = 'study_athletic',
  Unpaid = 'unpaid',
  UnpaidAgreed = 'unpaid_agreed',
  UnpaidMartialLaw = 'unpaid_martial_law',
  VacationAnnualOnboarding = 'vacation_annual_onboarding',
  VacationUnpaidOnboarding = 'vacation_unpaid_onboarding',
}
export enum TaskPeriodType {
  WorkingYear = 'working_year',
  CalendarYear = 'calendar_year',
}
export enum TaskTemplateStatus {
  Active = 'active',
  Inactive = 'inactive',
  Superseded = 'superseded',
  Archived = 'archived',
}
export enum TaskTemplateType {
  Vacation = 'vacation',
  VacationAnnual = 'vacation_annual',
  VacationAnnualHazardous = 'vacation_annual_hazardous',
  VacationAnnualSpecialWork = 'vacation_annual_special_work',
  VacationSocialMaternityLeave = 'vacation_social_maternity_leave',
  VacationSocialChildCare = 'vacation_social_child_care',
  VacationSocialParental = 'vacation_social_parental',
  VacationSocialChildBirth = 'vacation_social_child_birth',
  VacationSocialAdoption = 'vacation_social_adoption',
  VacationSocialPco = 'vacation_social_pco',
  VacationStudyEducational = 'vacation_study_educational',
  VacationStudyCreative = 'vacation_study_creative',
  VacationStudyAthletic = 'vacation_study_athletic',
  VacationUnpaid = 'vacation_unpaid',
  VacationUnpaidAgreed = 'vacation_unpaid_agreed',
  VacationUnpaidMartialLaw = 'vacation_unpaid_martial_law',
  VacationAnnualOnboarding = 'vacation_annual_onboarding',
  VacationUnpaidOnboarding = 'vacation_unpaid_onboarding',
  UniversalDocument = 'universal_document',
}
export enum TaskAttachmentType {
  BirthCertificate = 'birth_certificate',
  DisabilityCertificate = 'disability_certificate',
  SingleParentProof = 'single_parent_proof',
  AdoptionCourtDecision = 'adoption_court_decision',
  CohabitationProof = 'cohabitation_proof',
  MedicalConclusion = 'medical_conclusion',
  CombatParticipantId = 'combat_participant_id',
  EducationCallNotice = 'education_call_notice',
}
export enum MediaFileType {
  UserProfileImage = 'user_profile_image',
  EmployeeProfileImage = 'employee_profile_image',
  FileImport = 'file_import',
  TaskAttachment = 'task_attachment',
  DiiaMultisharingArchive = 'diia_multisharing_archive',
  DiiaDocument = 'diia_document',
}
export enum AntivirusCheckStatus {
  Checking = 'checking',
  Infected = 'infected',
  Clean = 'clean',
  Skip = 'skip',
}
export enum EmployeeDocumentType {
  InternalPassport = 'internal-passport',
  ForeignPassport = 'foreign-passport',
  TaxpayerCard = 'taxpayer-card',
  ReferenceInternallyDisplacedPerson = 'reference-internally-displaced-person',
  DriverLicense = 'driver-license',
  VehicleLicense = 'vehicle-license',
  UserBirthRecord = 'user-birth-record',
  BirthCertificate = 'birth-certificate',
  StudentIdCard = 'student-id-card',
  EducationDocument = 'education-document',
  PensionCard = 'pension-card',
  VeteranCertificate = 'veteran-certificate',
  ResidencePermitTemporary = 'residence-permit-temporary',
  ResidencePermitPermanent = 'residence-permit-permanent',
  MarriageActRecord = 'marriage-act-record',
  DivorceActRecord = 'divorce-act-record',
  NameChangeActRecord = 'name-change-act-record',
  IncomeReport = 'income-report',
}
export enum ImportTaskType {
  Employee = 'employee',
  Vacation = 'vacation',
}
export enum ImportTaskStatus {
  Pending = 'pending',
  Processing = 'processing',
  Success = 'success',
  Failed = 'failed',
}
export enum TaskDocumentType {
  Request = 'request',
  Application = 'application',
  Order = 'order',
  Universal = 'universal',
}
export enum PdfBlockAlign {
  Left = 'left',
  Right = 'right',
  Center = 'center',
}
export enum PdfBlockVerticalAlign {
  Top = 'top',
  Bottom = 'bottom',
  Center = 'center',
}
export enum HrsDocumentCategory {
  Act = 'act',
  Contract = 'contract',
  Application = 'application',
  Appendix = 'appendix',
  Another = 'another',
  Order = 'order',
  Report = 'report',
  Letter = 'letter',
}
export enum TaskDocumentAccessSettings {
  Extended = 'extended',
  Private = 'private',
}
export enum DocumentActionType {
  Sign = 'sign',
  Approve = 'approve',
  View = 'view',
}
export enum ActionExecutorType {
  Initiator = 'initiator',
  EmployeeLeader = 'employee_leader',
  EmployeeManagerialLeader = 'employee_managerial_leader',
  CompanyLeader = 'company_leader',
  Static = 'static',
}
export enum TaskTemplateBatch {
  Vacation = 'vacation',
  Documents = 'documents',
  VacationOnboarding = 'vacation_onboarding',
}
export enum TaskStatus {
  New = 'new',
  InProgress = 'in_progress',
  Completed = 'completed',
  Canceled = 'canceled',
}
export enum TaskRetrieveDocumentState {
  Generated = 'generated',
  Preview = 'preview',
}
export enum TaskOperationType {
  SendDocuments = 'send_documents',
  EditTask = 'edit_task',
  EditAction = 'edit_action',
  CancelTask = 'cancel_task',
  AddEmployeeInformation = 'add_employee_information',
}
export enum TaskErrorType {
  Missing = 'missing',
  InvalidStatus = 'invalid_status',
}
export enum TaskActivityStatus {
  Active = 'active',
  Archived = 'archived',
}
export enum TaskRouteType {
  Creation = 'creation',
  DocumentGeneration = 'document_generation',
  Signing = 'signing',
  Approval = 'approval',
}
export enum TaskRouteStatus {
  New = 'new',
  InProgress = 'in_progress',
  Completed = 'completed',
  Canceled = 'canceled',
}
export enum TaskRouteActionStatus {
  New = 'new',
  Completed = 'completed',
  Declined = 'declined',
}
export enum ActionValidityStatus {
  Valid = 'valid',
  Invalid = 'invalid',
}
export enum PublicTokenActiveStatus {
  Active = 'active',
  Inactive = 'inactive',
}
export enum CompanyPlanType {
  Free = 'free',
  Paid = 'paid',
}
export enum InternalUserRole {
  User = 'user',
  Admin = 'admin',
}
export enum PublicDocumentType {
  Signing = 'signing',
  Approval = 'approval',
}
export const {
  useGetProfileQuery,
  useGetAuthorizedUserQuery,
  useUpdateAuthorizedUserMutation,
  useUpdateAuthorizedUserPhotoMutation,
  useGetUserConfigQuery,
  useUpdateUserConfigMutation,
  useGetUserEmailNotificationSettingsQuery,
  useUpdateUserEmailNotificationSettingMutation,
  useCreateUserRoleMutation,
  useGetUserCompaniesQuery,
  useUpdateUserRoleMutation,
  useListCompanyUsersQuery,
  useDeactivateCompanyUserMutation,
  useUpdateCompanyMutation,
  useGetFullUserRolesQuery,
  useCountUserRolesQuery,
  useGetCompanyCheckListQuery,
  useGetCompaniesCheckListQuery,
  useDoneCompanyCheckListItemMutation,
  useCreateCompanyInviteMutation,
  useListEmployeesQuery,
  useCreateEmployeeMutation,
  useDeleteEmployeesMutation,
  useGetEmployeesFiltersQuery,
  useCountEmployeesQuery,
  useValidateEmployeeMutation,
  useRetrieveEmployeeQuery,
  useUpdateEmployeeMutation,
  usePartialUpdateEmployeeMutation,
  useDeleteEmployeeMutation,
  useInviteEmployeesMutation,
  useUpdateEmployeePhotoMutation,
  useCreateEmployeeAbsencesMutation,
  useListEmployeeAbsencesQuery,
  useUpdateEmployeeAbsencesMutation,
  useUpdateEmployeeWorkingPeriodMutation,
  useEmployeeUploadAttachmentMutation,
  useEmployeeListAttachmentQuery,
  useInviteEmployeeMutation,
  useRestoreEmployeeMutation,
  useChangeEmployeeSigningTypeMutation,
  useListEmployeeDocumentsQuery,
  useCreateGroupMutation,
  useListGroupsQuery,
  useUpdateGroupMutation,
  useCreateFileImportUploadMutation,
  useGetFileImportUploadQuery,
  useCreateVacationTaskTemplateMutation,
  useGenerateTaskTemplateDocumentPreviewMutation,
  useRetrieveVacationTaskTemplateQuery,
  useUpdateVacationTaskTemplateMutation,
  useUpdateVacationTaskTemplateManagerMutation,
  useGetTemplatePeriodsQuery,
  useListTaskTemplatesQuery,
  useListTaskTemplatesByBatchQuery,
  useToggleActiveTaskTemplatesMutation,
  useCountTasksQuery,
  useGetTaskFiltersQuery,
  useGetTaskDocumentPreviewQuery,
  useDeleteTaskDocumentMutation,
  useTaskUploadDocumentMutation,
  useListTaskDocumentsQuery,
  useGetTaskOperationsQuery,
  useGetTaskErrorsQuery,
  useCreateTaskMutation,
  useListTasksQuery,
  useDeleteTaskMutation,
  useRetrieveTaskQuery,
  useUpdateTaskMutation,
  useCancelTaskMutation,
  useSendDocumentsMutation,
  useGetTaskPeriodsQuery,
  useTaskUploadAttachmentMutation,
  useTaskFullRouteEditMutation,
  useTaskRouteDeleteMutation,
  useTaskRouteEditMutation,
  useTaskRouteDocumentPreviewQuery,
  useTaskRouteDocumentPreviewWithParametersMutation,
  useTaskRouteActionRemindToActionMutation,
  useGetUploadedFileQuery,
  useGetUploadedFileByIdQuery,
  useCreatePublicTokenMutation,
  useListCompanyPublicTokensQuery,
  useUpdatePublicTokenMutation,
  useDeletePublicTokenMutation,
  useCreateOnboardingAttemptMutation,
  useUpdateOnboardingAttemptMutation,
  useGetDiiaOnboardingDeeplinkQuery,
  useVchasnoMobileEmployeesQuery,
  useVchasnoMobileEmployeeByIdQuery,
  useVchasnoMobileEmployeeTaskTemplateQuery,
  useVchasnoMobileEmployeeTaskCreateMutation,
  useVchasnoMobileEmployeeTaskUpdateMutation,
  useVchasnoMobileEmployeeTaskQuery,
  useVchasnoMobileEmployeeTaskToActionQuery,
  useVchasnoMobileEmployeeTaskByIdQuery,
  useVchasnoMobileEmployeeTaskToActionCountQuery,
  useVchasnoMobileCancelTaskMutation,
  useVchasnoMobileApproveTaskMutation,
  useMobileGetUploadedFileQuery,
  useUpdateCompanyMaxEmployeesMutation,
  useListCompanyAdminQuery,
  useChangeCompanyPlanMutation,
  useResyncCompanyTemplatesMutation,
  useUpdateUserInternalRoleMutation,
  useAdminAddEdoRoleMutation,
  useAdminRemoveEdoRoleMutation,
  useAdminInviteEmployeeMutation,
  useCreatePublicApiTokenMutation,
  useUpdatePublicApiTokenMutation,
  useAdminUploadTaskTemplateDocumentMutation,
  useAdminSetTaskTemplateDocumentGenerationParamsMutation,
  usePublicCreateEmployeeMutation,
  usePublicListEmployeesQuery,
  usePublicUpdateEmployeeMutation,
  usePublicDeleteEmployeeMutation,
  usePublicGetEmployeeQuery,
  usePublicCreateEmployeeAbsencesMutation,
  usePublicListEmployeeAbsencesQuery,
  usePublicUpdateEmployeeAbsencesMutation,
  usePublicListGroupsQuery,
  usePublicCreateGroupMutation,
  usePublicGetGroupQuery,
  usePublicUpdateGroupMutation,
  usePublicDeleteGroupMutation,
  usePublicListVacationDocumentsQuery,
  usePublicListVacationDocumentSignersQuery,
  usePublicCreateVacationDocumentSignerMutation,
  usePublicDeleteVacationDocumentSignerMutation,
  usePublicSendVacationDocumentsMutation,
  usePublicListUniversalDocumentsQuery,
  usePublicListUniversalDocumentDocumentsQuery,
  usePublicListUniversalDocumentDocumentSignersQuery,
  usePublicCreateUniversalDocumentDocumentSignerMutation,
  usePublicDeleteUniversalDocumentDocumentSignerMutation,
  usePublicSendUniversalDocumentDocumentsMutation,
  useHandleFilesBatchUploadMutation,
  useGetEmployeeOnboardingMeQuery,
  useSendOnboardingOtpCodeMutation,
  useAuthenticateOnboardingMutation,
} = injectedRtkApi;
